import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TimelineTagsModal } from "@/components/TimelineTagsModal";
import {
  ShoppingCart,
  TrendingUp,
  Target,
  Users,
  FileText,
  Plus,
  Search,
  Filter,
  Download,
  Calendar,
  Eye,
  Pencil,
  Trash2,
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  Send,
  DollarSign,
  Building2,
  User,
  Phone,
  CreditCard,
  ArrowRight,
  BarChart3,
  RefreshCw,
  RefreshCcw,
  RotateCcw,
  Loader2,
  Factory,
  CircleDollarSign,
  Wallet,
  Banknote,
  Tags,
  Tag,
  Settings,
  ChevronDown,
  Check,
  X,
  History
} from "lucide-react";

const formatCurrency = (value) => {
  if (!value && value !== 0) return "R$ 0,00";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    return new Date(dateStr).toLocaleDateString("pt-BR");
  } catch {
    return dateStr;
  }
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const date = new Date(dateStr);
    return date.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return dateStr;
  }
};

const statusConfig = {
  PROPOSTA: { color: "bg-blue-100 text-blue-800", icon: FileText, label: "Proposta" },
  PRODUCAO: { color: "bg-cyan-100 text-cyan-800", icon: Clock, label: "Em Produção" },
  FLUXO_PORTABILIDADE: { color: "bg-indigo-100 text-indigo-800", icon: RefreshCcw, label: "Fluxo Portabilidade" },
  ANALISE: { color: "bg-yellow-100 text-yellow-800", icon: Clock, label: "Em Análise" },
  PENDENTE_DOCS: { color: "bg-orange-100 text-orange-800", icon: AlertCircle, label: "Pendente Docs" },
  APROVADO: { color: "bg-green-100 text-green-800", icon: CheckCircle, label: "Aprovado" },
  FORMALIZADO: { color: "bg-purple-100 text-purple-800", icon: Send, label: "Formalizado" },
  PAGO: { color: "bg-emerald-100 text-emerald-800", icon: DollarSign, label: "Pago" },
  REPROVADO: { color: "bg-red-100 text-red-800", icon: XCircle, label: "Reprovado" },
  RECONSIDERADO: { color: "bg-pink-100 text-pink-800", icon: RefreshCcw, label: "Reconsiderado" },
  ESTORNADO: { color: "bg-orange-100 text-orange-800", icon: XCircle, label: "Estornado" },
  CANCELADO: { color: "bg-red-100 text-red-800", icon: XCircle, label: "Cancelado" },
  RECUSADO: { color: "bg-gray-100 text-gray-800", icon: XCircle, label: "Recusado" }
};

const tiposOperacao = [
  { value: "EMPRESTIMO", label: "Empréstimo Consignado" },
  { value: "CARTAO_CONSIGNADO", label: "Cartão Consignado" },
  { value: "CARTAO_BENEFICIO", label: "Cartão Benefício" },
  { value: "PORTABILIDADE", label: "Portabilidade" },
  { value: "REFINANCIAMENTO", label: "Refinanciamento" },
  { value: "SAQUE_FGTS", label: "Saque FGTS" },
  { value: "SAQUE_ANIVERSARIO", label: "Saque Aniversário" }
];

// Função para mapear situação do contrato para status de venda
const mapearSituacaoParaStatus = (situacao) => {
  const mapa = {
    "EM_ANALISE": "ANALISE",
    "ATIVO": "APROVADO",
    "APROVADO": "APROVADO",
    "QUITADO": "PAGO",
    "REPROVADO": "REPROVADO",
    "ESTORNADO": "ESTORNADO",
    "CANCELADO": "REPROVADO",
    "SUSPENSO": "PENDENTE_DOCS",
    "PORTABILIDADE": "FORMALIZADO",
    "REFINANCIADO": "FORMALIZADO"
  };
  return mapa[situacao] || "PROPOSTA";
};

/**
 * Calcula comissões para um contrato baseado na tabela de comissão
 * @param {Object} venda - Dados do contrato/venda
 * @param {Array} tabelasComissao - Lista de tabelas de comissão disponíveis
 * @returns {Object} - Comissões calculadas em % e R$
 */
const calcularComissoes = (venda, tabelasComissao, valorRecebidoEmpresa = 0) => {
  const valorContrato = parseFloat(venda.valor_liberado || venda.valor_solicitado || 0);
  
  if (!tabelasComissao || tabelasComissao.length === 0 || valorContrato === 0) {
    return null;
  }
  
  // Buscar tabela de comissão compatível
  let tabelaCompativel = null;
  
  // 1. Tentar encontrar por tabela_comissao_id (se a venda já tem uma tabela vinculada)
  if (venda.tabela_comissao_id) {
    tabelaCompativel = tabelasComissao.find(t => t.id === venda.tabela_comissao_id);
  }
  
  // 2. Tentar encontrar pelo banco
  if (!tabelaCompativel && venda.banco) {
    tabelaCompativel = tabelasComissao.find(t => 
      t.banco_nome?.toUpperCase().includes(venda.banco?.toUpperCase()) ||
      t.banco_codigo === venda.banco_codigo
    );
  }
  
  // 3. Tentar encontrar pelo tipo de operação
  if (!tabelaCompativel && venda.tipo_operacao) {
    tabelaCompativel = tabelasComissao.find(t => 
      t.tipo_operacao?.toUpperCase() === venda.tipo_operacao?.toUpperCase()
    );
  }
  
  // 4. Usar a primeira tabela disponível com comissões (modo fallback)
  if (!tabelaCompativel) {
    tabelaCompativel = tabelasComissao.find(t => t.comissoes?.length > 0) || tabelasComissao[0];
  }
  
  // Verificar se a tabela tem comissões (pode estar em 'comissoes' ou 'itens')
  const comissoes = tabelaCompativel?.comissoes || tabelaCompativel?.itens || [];
  
  if (!tabelaCompativel || comissoes.length === 0) {
    return null;
  }
  
  // Buscar item compatível na tabela (por produto, tipo ou prazo)
  let itemComissao = comissoes.find(item => {
    // Verificar tipo de operação
    const tipoMatch = item.tipo && venda.tipo_operacao && 
      item.tipo?.toUpperCase().includes(venda.tipo_operacao?.toUpperCase());
    
    // Verificar produto
    const produtoMatch = item.produto && venda.tipo_operacao &&
      item.produto?.toUpperCase().includes(venda.tipo_operacao?.toUpperCase());
    
    // Verificar prazo
    const prazoMatch = item.prazo && venda.prazo && 
      parseInt(item.prazo) === parseInt(venda.prazo);
    
    return tipoMatch || produtoMatch || prazoMatch;
  });
  
  // Se não encontrou específico, usar o primeiro item da tabela
  if (!itemComissao) {
    itemComissao = comissoes[0];
  }
  
  // Obter percentuais (aceita ambos os formatos de nomenclatura)
  const empresaPerc = parseFloat(itemComissao.empresa || itemComissao.empresa_percentual || 0);
  const teleVendaPerc = parseFloat(itemComissao.teleVenda || itemComissao.tele_venda_percentual || 0);
  const corretorPerc = parseFloat(itemComissao.corretor || itemComissao.corretor_percentual || 0);
  const atendentePerc = parseFloat(itemComissao.atendente || itemComissao.atendente_percentual || 0);
  const gerAdmPerc = parseFloat(itemComissao.gerAdm || itemComissao.gerente_adm_percentual || 0);
  const gerOperPerc = parseFloat(itemComissao.gerOper || itemComissao.gerente_oper_percentual || 0);
  
  // Comissão da empresa: usa o valor REALMENTE recebido quando informado (igual ao Relatório); senão o esperado da tabela
  const comissaoEmpresaCalc = (valorContrato * empresaPerc) / 100;
  const comissaoEmpresaValor = (valorRecebidoEmpresa && valorRecebidoEmpresa > 0) ? valorRecebidoEmpresa : comissaoEmpresaCalc;
  
  // Modelo de cálculo (VALOR_CONTRATO ou COMISSAO_EMPRESA)
  const modeloCalculo = itemComissao.modeloCalculo || "COMISSAO_EMPRESA";
  
  // Função para calcular comissão individual
  const calcularComissaoIndividual = (percentual) => {
    if (!percentual) return { percentual: 0, valor: 0 };
    
    const base = modeloCalculo === "VALOR_CONTRATO" ? valorContrato : comissaoEmpresaValor;
    const valor = (base * percentual) / 100;
    
    return {
      percentual,
      valor
    };
  };
  
  return {
    tabela_nome: tabelaCompativel.nome || tabelaCompativel.nome_tabela || "Tabela",
    banco_nome: tabelaCompativel.banco_nome || "",
    produto_nome: itemComissao.produto || itemComissao.nome_tabela || "Produto",
    valor_contrato: valorContrato,
    empresa: {
      percentual: empresaPerc,
      valor: comissaoEmpresaValor
    },
    tele_venda: calcularComissaoIndividual(teleVendaPerc),
    corretor: calcularComissaoIndividual(corretorPerc),
    atendente: calcularComissaoIndividual(atendentePerc),
    gerente_adm: calcularComissaoIndividual(gerAdmPerc),
    gerente_oper: calcularComissaoIndividual(gerOperPerc)
  };
};

const getMesAtual = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const getMesAnterior = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

// Pendentes = falhas reais (Falta Doc/Digitar/Formalizar, Pendência Banco). NÃO inclui "Em Análise".
const _normTagVenda = (s) => (s || "").trim().toUpperCase().replace(/[_\s]+/g, " ").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const TAGS_PENDENTE_REAL = ["FALTA DOC", "FALTA DIGIT", "FALTA FORMALIZ", "PENDENCIA BANCO"];
const isPendenteReal = (v) => (v?.tags_gestao_nomes || []).some(t => TAGS_PENDENTE_REAL.some(k => _normTagVenda(t).includes(k)));

export default function Vendas() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const cpfParam = searchParams.get("cpf") || "";
  const [activeTab, setActiveTab] = useState("propostas");
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(cpfParam);
  const [filtroStatus, setFiltroStatus] = useState("ativos");
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [filtroTipo, setFiltroTipo] = useState("todos");
  const [filtroBanco, setFiltroBanco] = useState("todos");
  const [filtroOperador, setFiltroOperador] = useState("todos");
  const [filtroCorrespondente, setFiltroCorrespondente] = useState("todos");
  const [periodoFiltro, setPeriodoFiltro] = useState("mes");
  
  // Usuário logado
  const [usuarioLogado, setUsuarioLogado] = useState(null);
  
  // Data states
  const [resumo, setResumo] = useState({
    total_vendas: 0,
    valor_total: 0,
    ticket_medio: 0,
    taxa_conversao: 0,
    propostas_mes: 0,
    aprovadas_mes: 0,
    em_analise: 0,
    reprovadas: 0,
    estornadas: 0
  });
  const [vendas, setVendas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [operadores, setOperadores] = useState([]);
  const [tabelasComissao, setTabelasComissao] = useState([]);
  const [comissoes, setComissoes] = useState([]); // Estado para comissões
  const [tagsContratos, setTagsContratos] = useState([]); // Tags de gestão de contratos
  
  // Modal states
  const [showDetalhes, setShowDetalhes] = useState(false);
  const [vendaSelecionada, setVendaSelecionada] = useState(null);
  
  // Modal de Reprovação
  const [showReprovacao, setShowReprovacao] = useState(false);
  const [contratoParaReprovar, setContratoParaReprovar] = useState(null);
  const [motivoReprovacao, setMotivoReprovacao] = useState("");
  
  // Modal de Reconsideração
  const [showReconsideracao, setShowReconsideracao] = useState(false);
  const [contratoParaReconsiderar, setContratoParaReconsiderar] = useState(null);
  const [motivoReconsideracao, setMotivoReconsideracao] = useState("");
  
  // Modal de Estorno
  const [showEstorno, setShowEstorno] = useState(false);
  const [contratoParaEstornar, setContratoParaEstornar] = useState(null);
  const [motivoEstorno, setMotivoEstorno] = useState("");
  
  // Modal de Clientes por Banco
  const [showClientesBanco, setShowClientesBanco] = useState(false);
  const [bancoSelecionado, setBancoSelecionado] = useState(null);
  const [clientesDoBanco, setClientesDoBanco] = useState([]);

  // Estados para gerenciamento de tags
  const [filtroTagVendas, setFiltroTagVendas] = useState("TODAS");
  const [showConfigTagsVendas, setShowConfigTagsVendas] = useState(false);
  const [editandoTagVendas, setEditandoTagVendas] = useState(null);
  const [novaTagVendas, setNovaTagVendas] = useState({ nome: "", cor: "#3B82F6", bgCor: "#EFF6FF" });
  const [salvandoTagVendas, setSalvandoTagVendas] = useState(false);

  // Timeline modal
  const [showTimelineContrato, setShowTimelineContrato] = useState(false);
  const [timelineContrato, setTimelineContrato] = useState(null);

  // Cores predefinidas para tags
  const CORES_TAGS = [
    { cor: "#3B82F6", bgCor: "#EFF6FF", nome: "Azul" },
    { cor: "#8B5CF6", bgCor: "#F5F3FF", nome: "Roxo" },
    { cor: "#10B981", bgCor: "#ECFDF5", nome: "Verde" },
    { cor: "#F59E0B", bgCor: "#FFFBEB", nome: "Amarelo" },
    { cor: "#EF4444", bgCor: "#FEF2F2", nome: "Vermelho" },
    { cor: "#06B6D4", bgCor: "#ECFEFF", nome: "Ciano" },
    { cor: "#EC4899", bgCor: "#FDF2F7", nome: "Rosa" },
    { cor: "#64748B", bgCor: "#F8FAFC", nome: "Cinza" },
    { cor: "#059669", bgCor: "#D1FAE5", nome: "Verde Escuro" },
    { cor: "#6366F1", bgCor: "#EEF2FF", nome: "Indigo" },
  ];

  // Carregar usuário logado
  useEffect(() => {
    const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");
    setUsuarioLogado(usuario);
  }, []);

  useEffect(() => {
    const delay = (searchTerm && searchTerm.trim().length >= 3) ? 400 : 0;
    const t = setTimeout(() => { carregarDados(); }, delay);
    return () => clearTimeout(t);
  }, [periodoFiltro, searchTerm]);

  useEffect(() => {
    setPaginaAtual(1);
  }, [filtroStatus, filtroTipo, filtroBanco, filtroOperador, filtroCorrespondente, searchTerm, filtroTagVendas, vendas]);

  const carregarDados = async () => {
    setLoading(true);
    try {
      // MULTI-TENANCY: Obter empresa do contexto
      const empresaAtual = JSON.parse(localStorage.getItem("empresa") || "null");
      const empresaParam = empresaAtual?.id ? `&empresa_id=${empresaAtual.id}` : "";
      
      // Verificar perfil do usuário
      const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");
      const perfilUsuario = (usuario.perfil || "").toUpperCase();
      const isCorretor = perfilUsuario === "CORRETOR";
      // ATENDENTE vê todos os contratos, CORRETOR só vê os dele
      
      // Filtro por operador - APENAS para CORRETOR (nos contratos)
      let filtroOperadorContrato = "";
      if (isCorretor && usuario.nome) {
        const nomeBusca = usuario.codigo_usuario ? `${usuario.codigo_usuario} ${usuario.nome}`.trim() : usuario.nome;
        filtroOperadorContrato = `&operador=${encodeURIComponent(nomeBusca)}`;
      }
      
      // Filtro de comissão - ATENDENTE e CORRETOR só veem suas próprias comissões
      const isAtendenteOuCorretor = perfilUsuario === "ATENDENTE" || perfilUsuario === "CORRETOR";
      let filtroOperadorComissao = "";
      if (isAtendenteOuCorretor && usuario.nome) {
        const nomeBusca = usuario.codigo_usuario ? `${usuario.codigo_usuario} ${usuario.nome}`.trim() : usuario.nome;
        filtroOperadorComissao = `&operador=${encodeURIComponent(nomeBusca)}`;
      }
      
      // Filtro de mes (padrao: mes atual). "todos" carrega tudo (apenas Gestao).
      // BUSCA GLOBAL: ao buscar (CPF/Nº contrato), ignora o mês e busca em todo o histórico.
      const buscandoGlobal = searchTerm && searchTerm.trim().length >= 3;
      const mesParam = buscandoGlobal ? "" : (periodoFiltro && periodoFiltro !== "todos" ? `&mes=${periodoFiltro === "mes_anterior" ? getMesAnterior() : getMesAtual()}` : "");
      const [contratosRes, clientesRes, bancosRes, tabelasRes, usuariosRes, comissoesRes, tagsRes, contadoresRes] = await Promise.all([
        axios.get(`${API}/contratos?per_page=2000&tipo=INTERNO&incluir_ocultos=true${empresaParam}${filtroOperadorContrato}${mesParam}`),
        axios.get(`${API}/clientes?limit=100${empresaParam}`),
        axios.get(`${API}/bancos`),
        axios.get(`${API}/tabelas-comissao`),
        axios.get(`${API}/usuarios-sistema`),
        axios.get(`${API}/financeiro/comissoes?periodo=ano${filtroOperadorComissao}`), // Comissões filtradas por operador
        axios.get(`${API}/tags-gestao-contratos`), // Tags de gestão de contratos
        axios.get(`${API}/vendas/contadores?tipo=INTERNO${empresaParam}${filtroOperadorContrato}${mesParam}`)
      ]);
      
      // Salvar tags de contratos
      setTagsContratos(tagsRes.data || []);
      
      // Salvar comissões
      setComissoes(comissoesRes.data || []);
      
      // Salvar tabelas de comissão
      setTabelasComissao(tabelasRes.data?.tabelas || []);
      
      // Salvar operadores (usuários)
      const usuariosData = usuariosRes.data?.usuarios || usuariosRes.data || [];
      setOperadores(usuariosData);
      
      // Processar contratos para formato de vendas - APENAS INTERNOS
      const todosContratos = contratosRes.data?.contratos || contratosRes.data || [];
      const contratosData = todosContratos;
      
      // Normaliza: remove acentos, upper case, troca espaço/underscore
      const norm = (s) => (s || "").trim().toUpperCase().replace(/[_\s]+/g, " ").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      
      // Tags de status
      const tagsProd = ["FALTA DIGITAR", "FALTA FORMALIZAR", "FALTA DOCUMENTACAO", "DESBLOQUEIO INSS 60", "DESBLOQUEIO INSS 90"];
      const tagsPort = ["ENVIADO CIP SALDO DEVEDOR", "ACEITE CIP REFIN PORTABILIDADE", "AGUARDA MARGEM REDU"];
      
      const temTag = (c, tags) => {
        const nomes = c.tags_gestao_nomes || [];
        return nomes.some(t => tags.some(tp => norm(t).includes(tp)));
      };
      
      // Filtrar contratos por tags/situação
      const contratosEmProducao = contratosData.filter(c => temTag(c, tagsProd));
      const contratosFluxoPort = contratosData.filter(c => temTag(c, tagsPort));
      const contratosAprovados = contratosData.filter(c => !temTag(c, tagsProd) && !temTag(c, tagsPort) && (c.situacao === "ATIVO" || c.situacao === "APROVADO" || c.esteira_contrato === "APROVADO"));
      const contratosReprovados = contratosData.filter(c => !temTag(c, tagsProd) && !temTag(c, tagsPort) && (c.situacao === "REPROVADO" || c.situacao === "CANCELADO" || c.esteira_contrato === "REPROVADO"));
      const contratosEstornados = contratosData.filter(c => !temTag(c, tagsProd) && !temTag(c, tagsPort) && (c.situacao === "ESTORNADO" || c.esteira_contrato === "ESTORNADO"));
      const contratosEmAnalise = contratosData.filter(c => !temTag(c, tagsProd) && !temTag(c, tagsPort) && !contratosAprovados.includes(c) && !contratosReprovados.includes(c) && !contratosEstornados.includes(c));
      
      // Calcular valores por situação
      const valorEmProducao = contratosEmProducao.reduce((acc, c) => acc + (c.valor_liberado || c.valor_solicitado || 0), 0);
      const valorFluxoPort = contratosFluxoPort.reduce((acc, c) => acc + (c.valor_liberado || c.valor_solicitado || 0), 0);
      const valorEmAnalise = contratosEmAnalise.reduce((acc, c) => acc + (c.valor_liberado || c.valor_solicitado || 0), 0);
      const valorAprovados = contratosAprovados.reduce((acc, c) => acc + (c.valor_liberado || c.valor_solicitado || 0), 0);
      const valorReprovados = contratosReprovados.reduce((acc, c) => acc + (c.valor_liberado || c.valor_solicitado || 0), 0);
      const valorEstornados = contratosEstornados.reduce((acc, c) => acc + (c.valor_liberado || c.valor_solicitado || 0), 0);
      
      // Calcular taxa de conversão por etapa
      const taxaAprovacao = contratosData.length > 0 ? (contratosAprovados.length / contratosData.length * 100).toFixed(1) : 0;
      const taxaReprovacao = contratosData.length > 0 ? (contratosReprovados.length / contratosData.length * 100).toFixed(1) : 0;
      const taxaEstorno = contratosData.length > 0 ? (contratosEstornados.length / contratosData.length * 100).toFixed(1) : 0;
      const taxaEmAnalise = contratosData.length > 0 ? (contratosEmAnalise.length / contratosData.length * 100).toFixed(1) : 0;
      const taxaEmProducao = contratosData.length > 0 ? (contratosEmProducao.length / contratosData.length * 100).toFixed(1) : 0;
      const taxaFluxoPort = contratosData.length > 0 ? (contratosFluxoPort.length / contratosData.length * 100).toFixed(1) : 0;
      
      // Contar clientes únicos
      const clientesUnicos = new Set(contratosData.map(c => c.cliente_id)).size;
      
      setResumo({
        total_vendas: contratosData.length,
        clientes_unicos: clientesUnicos,
        valor_total: valorAprovados,
        ticket_medio: contratosAprovados.length > 0 ? valorAprovados / contratosAprovados.length : 0,
        taxa_conversao: taxaAprovacao,
        propostas_mes: contratosData.length,
        // Em Produção (1ª Etapa)
        em_producao: contratosEmProducao.length,
        valor_em_producao: valorEmProducao,
        taxa_em_producao: taxaEmProducao,
        // Fluxo Portabilidade (2ª Etapa)
        fluxo_port: contratosFluxoPort.length,
        valor_fluxo_port: valorFluxoPort,
        taxa_fluxo_port: taxaFluxoPort,
        // Em Análise (3ª Etapa)
        em_analise: contratosEmAnalise.length,
        valor_em_analise: valorEmAnalise,
        taxa_em_analise: taxaEmAnalise,
        // Aprovadas
        aprovadas_mes: contratosAprovados.length,
        valor_aprovadas: valorAprovados,
        taxa_aprovacao: taxaAprovacao,
        // Reprovadas
        reprovadas: contratosReprovados.length,
        valor_reprovadas: valorReprovados,
        taxa_reprovacao: taxaReprovacao,
        // Estornadas
        estornadas: contratosEstornados.length,
        valor_estornadas: valorEstornados,
        taxa_estorno: taxaEstorno
      });
      
      // Contadores otimizados no servidor (autoritativo, nao trava a lista)
      if (contadoresRes?.data) setResumo(contadoresRes.data);
      
      // Mapear contratos para formato de vendas
      const vendasMapeadas = contratosData.map(c => {
        // Buscar nome da tabela de comissão
        const tabela = tabelasRes.data?.tabelas?.find(t => t.id === c.tabela_comissao_id);
        const tabelaNome = tabela?.nome || c.tabela_nome || null;
        
        return {
          id: c.id,
          codigo: c.numero_contrato,
          cliente_nome: c.cliente_nome || "Cliente",
          cliente_cpf: c.cliente_cpf || "",
          cliente_id: c.cliente_id,
          banco_codigo: c.banco_codigo,
          banco_nome: c.banco_nome,
          tipo_operacao: c.tipo_operacao,
          tipo: c.tipo || "INTERNO",
          valor_solicitado: c.valor_solicitado || 0,
          valor_liberado: c.valor_liberado || 0,
          saldo_devedor: c.saldo_devedor || 0,
          prazo: c.quantidade_parcelas || 0,
          valor_parcela: c.valor_parcela || 0,
          status: mapearSituacaoParaStatus(c.situacao),
          situacao: c.situacao,
          esteira_contrato: c.esteira_contrato || "", // Para filtrar por esteira
          data_criacao: c.created_at,
          data_contrato: c.data_inicio,
          data_primeiro_vencimento: c.data_primeiro_vencimento,
          vendedor: c.operador_comissao || c.vendedor || "",
          instituicao: c.instituicao,
          correspondente: c.correspondente,
          tabela_nome: tabelaNome,
          produto_nome: c.produto_nome, // Nome do produto selecionado
          tabela_comissao_id: c.tabela_comissao_id,
          filial: c.filial_recepcao,
          segmento: c.segmento,
          tipo_contrato: c.tipo_contrato,
          // Dados bancários
          banco_pagamento: c.banco_pagamento,
          forma_pagamento: c.forma_pagamento,
          agencia: c.agencia,
          conta_corrente: c.conta_corrente,
          celular_formalizacao: c.celular_formalizacao,
          // Datas de mudança de status
          data_em_analise: c.data_em_analise || c.created_at,
          data_aprovacao: c.data_aprovacao,
          data_reprovacao: c.data_reprovacao,
          data_estorno: c.data_estorno,
          // Operadores
          operador_aprovacao: c.operador_aprovacao,
          operador_reprovacao: c.operador_reprovacao,
          operador_estorno: c.operador_estorno,
          // Observações
          observacoes: c.observacoes || "",
          observacoes_relatorio: c.observacoes_relatorio || "",
          motivo_reprovacao: c.motivo_reprovacao || "",
          motivo_estorno: c.motivo_estorno || "",
          motivo: c.motivo_reprovacao || c.motivo_estorno || c.observacoes || "",
          // Tags de gestão
          tags_gestao: c.tags_gestao || [],
          tags_gestao_nomes: c.tags_gestao_nomes || []
        };
      });
      
      setVendas(vendasMapeadas);
      setClientes(clientesRes.data?.clientes || clientesRes.data || []);
      setBancos(bancosRes.data || []);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      // Dados mock para demonstração
      setResumo({
        total_vendas: 0,
        valor_total: 0,
        ticket_medio: 0,
        taxa_conversao: 0,
        propostas_mes: 0,
        aprovadas_mes: 0,
        em_producao: 0,
        em_analise: 0,
        reprovadas: 0,
        estornadas: 0
      });
      setVendas([]);
    } finally {
      setLoading(false);
    }
  };

  // ==================== FUNÇÕES DE GERENCIAMENTO DE TAGS ====================
  
  // Contar vendas por tag
  const contadorPorTagVendas = vendas.reduce((acc, venda) => {
    const tagsVenda = venda.tags_gestao || [];
    tagsVenda.forEach(tagId => {
      acc[tagId] = (acc[tagId] || 0) + 1;
    });
    if (tagsVenda.length === 0) {
      acc["SEM_TAG"] = (acc["SEM_TAG"] || 0) + 1;
    }
    return acc;
  }, {});

  // Filtrar vendas por tag
  const vendasFiltradasPorTag = filtroTagVendas === "TODAS" 
    ? vendas 
    : filtroTagVendas === "PENDENTES"
    ? vendas.filter(isPendenteReal)
    : vendas.filter(v => (v.tags_gestao || []).includes(filtroTagVendas));

  // Adicionar/Remover tag do contrato
  const toggleTagVenda = async (vendaId, tagId, adicionar) => {
    try {
      const endpoint = adicionar ? "add-tag" : "remove-tag";
      await axios.post(`${API}/contratos/${vendaId}/${endpoint}`, {
        tag_id: tagId
      });
      
      // Atualizar estado local
      setVendas(prev => prev.map(v => {
        if (v.id === vendaId) {
          const tagsAtuais = v.tags_gestao || [];
          const tagsNomes = v.tags_gestao_nomes || [];
          const tag = tagsContratos.find(t => t.id === tagId);
          
          if (adicionar && !tagsAtuais.includes(tagId)) {
            return { 
              ...v, 
              tags_gestao: [...tagsAtuais, tagId],
              tags_gestao_nomes: [...tagsNomes, tag?.nome]
            };
          } else if (!adicionar) {
            return { 
              ...v, 
              tags_gestao: tagsAtuais.filter(t => t !== tagId),
              tags_gestao_nomes: tagsNomes.filter((_, i) => tagsAtuais[i] !== tagId)
            };
          }
        }
        return v;
      }));
      
      toast.success(adicionar ? "Tag adicionada!" : "Tag removida!");
    } catch (error) {
      console.error("Erro ao atualizar tag:", error);
      toast.error("Erro ao atualizar tag");
    }
  };

  // Criar nova tag
  const criarTagVendas = async () => {
    if (!novaTagVendas.nome.trim()) {
      toast.error("Digite o nome da tag");
      return;
    }
    
    setSalvandoTagVendas(true);
    try {
      const res = await axios.post(`${API}/tags-gestao-contratos`, novaTagVendas);
      setTagsContratos(prev => [...prev, res.data]);
      setNovaTagVendas({ nome: "", cor: "#3B82F6", bgCor: "#EFF6FF" });
      toast.success("Tag criada com sucesso!");
    } catch (error) {
      console.error("Erro ao criar tag:", error);
      toast.error("Erro ao criar tag");
    } finally {
      setSalvandoTagVendas(false);
    }
  };

  // Atualizar tag
  const atualizarTagVendas = async () => {
    if (!editandoTagVendas?.nome.trim()) {
      toast.error("Digite o nome da tag");
      return;
    }
    
    setSalvandoTagVendas(true);
    try {
      const res = await axios.put(`${API}/tags-gestao-contratos/${editandoTagVendas.id}`, editandoTagVendas);
      setTagsContratos(prev => prev.map(t => t.id === editandoTagVendas.id ? res.data : t));
      setEditandoTagVendas(null);
      toast.success("Tag atualizada!");
    } catch (error) {
      console.error("Erro ao atualizar tag:", error);
      toast.error("Erro ao atualizar tag");
    } finally {
      setSalvandoTagVendas(false);
    }
  };

  // Excluir tag
  const excluirTagVendas = async (tagId) => {
    if (!confirm("Tem certeza que deseja excluir esta tag? Ela será removida de todos os contratos.")) {
      return;
    }
    
    try {
      await axios.delete(`${API}/tags-gestao-contratos/${tagId}`);
      setTagsContratos(prev => prev.filter(t => t.id !== tagId));
      toast.success("Tag excluída!");
      carregarDados();
    } catch (error) {
      console.error("Erro ao excluir tag:", error);
      toast.error("Erro ao excluir tag");
    }
  };

  const atualizarStatus = async (vendaId, novoStatus) => {
    try {
      // Mapear status de venda para situação de contrato
      const mapaStatusParaSituacao = {
        "ANALISE": "EM_ANALISE",
        "APROVADO": "ATIVO",
        "PAGO": "QUITADO",
        "REPROVADO": "REPROVADO",
        "ESTORNADO": "ESTORNADO",
        "CANCELADO": "REPROVADO",
        "PENDENTE_DOCS": "SUSPENSO",
        "FORMALIZADO": "ATIVO",
        "PROPOSTA": "EM_ANALISE",
        "RECUSADO": "REPROVADO"
      };
      
      // Mapear status para esteira_contrato também
      const mapaStatusParaEsteira = {
        "ANALISE": "EM_ANALISE",
        "APROVADO": "APROVADO",
        "REPROVADO": "REPROVADO",
        "ESTORNADO": "ESTORNADO",
        "RECONSIDERADO": "RECONSIDERADO"
      };
      
      const situacao = mapaStatusParaSituacao[novoStatus] || novoStatus;
      const esteira_contrato = mapaStatusParaEsteira[novoStatus];
      
      // Enviar tanto situação quanto esteira_contrato
      const updateData = { situacao };
      if (esteira_contrato) {
        updateData.esteira_contrato = esteira_contrato;
      }
      
      await axios.put(`${API}/contratos/${vendaId}`, updateData);
      toast.success("Status atualizado com sucesso!");
      carregarDados();
    } catch (error) {
      toast.error("Erro ao atualizar status");
      console.error(error);
    }
  };

  // Abrir modal de reprovação
  const abrirModalReprovacao = (venda) => {
    setContratoParaReprovar(venda);
    setMotivoReprovacao("");
    setShowReprovacao(true);
  };

  // Confirmar reprovação com motivo (salva como histórico em OBS)
  const confirmarReprovacao = async () => {
    if (!motivoReprovacao.trim()) {
      toast.error("Informe o motivo da reprovação");
      return;
    }
    
    try {
      const agora = new Date().toLocaleString("pt-BR");
      const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");
      const obsHistorico = `[REPROVADO - ${agora} - ${usuario.nome || "Sistema"}] ${motivoReprovacao.trim()}`;
      
      // Append ao campo observacoes existente
      const obsAtual = contratoParaReprovar.observacoes || "";
      const novaObs = obsAtual ? `${obsAtual}\n${obsHistorico}` : obsHistorico;
      
      await axios.put(`${API}/contratos/${contratoParaReprovar.id}`, { 
        situacao: "REPROVADO",
        esteira_contrato: "REPROVADO",
        observacoes: novaObs,
        motivo_reprovacao: motivoReprovacao.trim()
      });
      toast.success("Contrato reprovado com sucesso!");
      setShowReprovacao(false);
      setContratoParaReprovar(null);
      setMotivoReprovacao("");
      carregarDados();
    } catch (error) {
      toast.error("Erro ao reprovar contrato");
      console.error(error);
    }
  };

  // Abrir modal de reconsideração
  const abrirModalReconsideracao = (venda) => {
    setContratoParaReconsiderar(venda);
    setMotivoReconsideracao("");
    setShowReconsideracao(true);
  };

  // Confirmar reconsideração (volta para Em Análise, salva histórico em OBS)
  const confirmarReconsideracao = async () => {
    if (!motivoReconsideracao.trim()) {
      toast.error("Informe o motivo da reconsideração");
      return;
    }
    
    try {
      const agora = new Date().toLocaleString("pt-BR");
      const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");
      
      // Snapshot dos dados do contrato anterior
      const c = contratoParaReconsiderar;
      const snapshot = [
        `[RECONSIDERADO - ${agora} - ${usuario.nome || "Sistema"}] ${motivoReconsideracao.trim()}`,
        `  Dados anteriores: Banco: ${c.banco_nome || c.banco_id || '-'} | Tipo: ${c.tipo_operacao || '-'} | Tabela: ${c.tabela_nome || '-'}`,
        `  Valores: Solicitado: R$ ${(c.valor_solicitado || 0).toFixed(2)} | Liberado: R$ ${(c.valor_liberado || 0).toFixed(2)} | Parcela: R$ ${(c.valor_parcela || 0).toFixed(2)} (${c.prazo || '-'}x)`,
        `  Contrato: ${c.numero_contrato || c.codigo || '-'} | Correspondente: ${c.correspondente || '-'}`,
      ].join('\n');
      
      const obsAtual = c.observacoes || "";
      const novaObs = obsAtual ? `${obsAtual}\n${snapshot}` : snapshot;
      
      await axios.put(`${API}/contratos/${c.id}`, { 
        situacao: "EM_ANALISE",
        esteira_contrato: "EM_ANALISE",
        observacoes: novaObs,
        motivo_reconsideracao: motivoReconsideracao.trim()
      });
      toast.success("Contrato reconsiderado! Voltou para Em Análise.");
      setShowReconsideracao(false);
      setContratoParaReconsiderar(null);
      setMotivoReconsideracao("");
      carregarDados();
    } catch (error) {
      toast.error("Erro ao reconsiderar contrato");
      console.error(error);
    }
  };

  // Abrir modal de estorno
  const abrirModalEstorno = (venda) => {
    setContratoParaEstornar(venda);
    setMotivoEstorno("");
    setShowEstorno(true);
  };

  // Confirmar estorno com motivo
  const confirmarEstorno = async () => {
    if (!motivoEstorno.trim()) {
      toast.error("Informe o motivo do estorno");
      return;
    }
    
    try {
      // Usar o novo endpoint que cria débitos automaticamente
      const response = await axios.post(`${API}/contratos/${contratoParaEstornar.id}/estornar`, { 
        motivo_estorno: motivoEstorno,
        operador_estorno: "Sistema" // TODO: usar nome do usuário logado
      });
      
      if (response.data.debitos_criados && response.data.debitos_criados.length > 0) {
        const debitosMsg = response.data.debitos_criados
          .map(d => `${d.beneficiario}: ${new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(d.valor)}`)
          .join("\n");
        toast.success(`Contrato estornado!\n\nDébitos gerados:\n${debitosMsg}`, { duration: 5000 });
      } else {
        toast.success("Contrato estornado com sucesso!");
      }
      
      setShowEstorno(false);
      setContratoParaEstornar(null);
      setMotivoEstorno("");
      carregarDados();
    } catch (error) {
      toast.error("Erro ao estornar contrato");
      console.error(error);
    }
  };

  // Excluir contrato
  const excluirContrato = async (contratoId) => {
    try {
      await axios.delete(`${API}/contratos/${contratoId}`);
      toast.success("Contrato excluído com sucesso!");
      setShowDetalhes(false);
      carregarDados();
    } catch (error) {
      const mensagemErro = error.response?.data?.detail || "Erro ao excluir contrato";
      toast.error(mensagemErro);
      console.error(error);
    }
  };

  // Função para mostrar clientes de um banco
  const mostrarClientesDoBanco = (bancoNome, vendasDoBanco) => {
    setBancoSelecionado(bancoNome);
    setClientesDoBanco(vendasDoBanco);
    setShowClientesBanco(true);
  };

  // Função para determinar o status de exibição baseado na esteira_contrato
  const getStatusFromEsteira = (venda) => {
    const esteira = venda?.esteira_contrato;
    const nomes = venda?.tags_gestao_nomes || [];
    
    // Normaliza: remove acentos, upper case, troca espaço/underscore
    const norm = (s) => (s || "").trim().toUpperCase().replace(/[_\s]+/g, " ").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    
    // Tags de Em Produção
    const tagsProd = ["FALTA DIGITAR", "FALTA FORMALIZAR", "FALTA DOCUMENTACAO", "DESBLOQUEIO INSS 60", "DESBLOQUEIO INSS 90"];
    const temTagProd = nomes.some(t => tagsProd.some(tp => norm(t).includes(tp)));
    
    // Tags de Fluxo Portabilidade
    const tagsPort = ["ENVIADO CIP SALDO DEVEDOR", "ACEITE CIP REFIN PORTABILIDADE", "AGUARDA MARGEM REDU"];
    const temTagPort = nomes.some(t => tagsPort.some(tp => norm(t).includes(tp)));
    
    // PRIORIDADE 1: decisão mais recente por DATA manda no status master (linha do tempo)
    const _decisoes = [
      { status: "APROVADO", d: venda?.data_aprovacao },
      { status: "REPROVADO", d: venda?.data_reprovacao },
      { status: "RECONSIDERADO", d: venda?.data_reconsideracao },
      { status: "ESTORNADO", d: venda?.data_estorno },
    ].filter(x => x.d && !isNaN(new Date(x.d).getTime()));
    if (_decisoes.length > 0) {
      _decisoes.sort((a, b) => new Date(a.d).getTime() - new Date(b.d).getTime());
      return _decisoes[_decisoes.length - 1].status;
    }

    // PRIORIDADE 2: tags de workflow (quando ainda não houve decisão datada)
    if (temTagProd) return "PRODUCAO";
    if (temTagPort) return "FLUXO_PORTABILIDADE";
    
    // Status finais (ações do contrato)
    if (esteira === "APROVADO" || venda?.situacao === "ATIVO" || venda?.situacao === "APROVADO") return "APROVADO";
    if (esteira === "REPROVADO" || venda?.situacao === "REPROVADO" || venda?.situacao === "CANCELADO") return "REPROVADO";
    if (esteira === "RECONSIDERADO" || venda?.situacao === "RECONSIDERADO") return "RECONSIDERADO";
    if (esteira === "ESTORNADO" || venda?.situacao === "ESTORNADO") return "ESTORNADO";
    
    // Sem tags de produção/portabilidade e sem status final = Em Análise
    return "ANALISE";
  };

  const getStatusBadge = (venda) => {
    // Se recebeu um objeto venda, determina o status pela esteira
    const status = typeof venda === 'object' ? getStatusFromEsteira(venda) : venda;
    const config = statusConfig[status] || statusConfig.PROPOSTA;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  // Monta a linha do tempo do contrato ORDENADA por data (evento mais recente por último)
  const construirTimeline = (venda) => {
    const eventos = [];
    const add = (label, dateVal, dot, textCls = "text-gray-600") => {
      const ts = dateVal ? new Date(dateVal).getTime() : NaN;
      eventos.push({ label, dateVal: dateVal || null, ts: isNaN(ts) ? null : ts, dot, textCls });
    };
    const st = getStatusFromEsteira(venda);
    add("Cadastro", venda.data_criacao, "bg-blue-400");
    add("Em Produção", venda.data_producao || venda.data_criacao, st === "PRODUCAO" ? "bg-cyan-500 ring-2 ring-cyan-200" : "bg-cyan-400");
    if (venda.data_em_analise || venda.esteira_contrato === "EM_ANALISE") {
      add("Em Análise", venda.data_em_analise, venda.esteira_contrato === "EM_ANALISE" ? "bg-yellow-500 ring-2 ring-yellow-200" : "bg-yellow-400");
    }
    if (venda.data_aprovacao || venda.situacao === "ATIVO" || venda.esteira_contrato === "APROVADO") {
      add("Aprovado", venda.data_aprovacao, (venda.situacao === "ATIVO" || venda.esteira_contrato === "APROVADO") ? "bg-green-500 ring-2 ring-green-200" : "bg-green-400");
    }
    const comissao = comissoes.find(c => c.contrato_id === venda.id);
    if (comissao) {
      const temRecebimento = comissao.status_recebimento === "RECEBIDO" || comissao.data_recebimento || comissao.status === "PENDENTE_PAGAMENTO" || comissao.status === "PAGO" || comissao.status === "ESTORNADO";
      const temPagamento = comissao.status_pagamento === "PAGO" || comissao.data_pagamento || comissao.status === "PAGO" || comissao.status === "ESTORNADO";
      const isPendRecebimento = comissao.status === "PENDENTE_RECEBIMENTO" || (comissao.status_recebimento === "PENDENTE" && !temRecebimento);
      const isPendPagamento = comissao.status === "PENDENTE_PAGAMENTO" || (comissao.status_pagamento === "PENDENTE" && temRecebimento && !temPagamento);
      const isPagoC = comissao.status === "PAGO" || (comissao.status_pagamento === "PAGO" && comissao.status !== "ESTORNADO");
      add("Pend. Com. Empresa", comissao.data, isPendRecebimento ? "bg-orange-500 ring-2 ring-orange-200" : temRecebimento ? "bg-orange-400" : "bg-gray-300", isPendRecebimento ? "text-orange-600 font-medium" : "text-gray-600");
      if (temRecebimento) add("Rec. Empresa", comissao.data_recebimento, "bg-teal-500");
      if (temRecebimento) add("Pend. Pag. Equipe", comissao.data_recebimento, isPendPagamento ? "bg-yellow-500 ring-2 ring-yellow-200" : temPagamento ? "bg-yellow-400" : "bg-gray-300", isPendPagamento ? "text-yellow-600 font-medium" : "text-gray-600");
      if (temPagamento) add("Pago Equipe", comissao.data_pagamento, isPagoC ? "bg-emerald-500 ring-2 ring-emerald-200" : "bg-emerald-400", isPagoC ? "text-emerald-600 font-medium" : "text-gray-600");
    }
    if (venda.data_reprovacao || venda.situacao === "REPROVADO" || venda.esteira_contrato === "REPROVADO") {
      add("Reprovado", venda.data_reprovacao, (venda.situacao === "REPROVADO" || venda.esteira_contrato === "REPROVADO") ? "bg-red-500 ring-2 ring-red-200" : "bg-red-400");
    }
    if (venda.data_reconsideracao || venda.esteira_contrato === "RECONSIDERADO") {
      add("Reconsiderado", venda.data_reconsideracao, "bg-pink-500");
    }
    if (venda.data_estorno || venda.situacao === "ESTORNADO" || venda.esteira_contrato === "ESTORNADO") {
      add("Estornado", venda.data_estorno, "bg-orange-500");
    }
    // Ordena por data crescente; eventos sem data ficam no início preservando ordem de inserção
    eventos.sort((a, b) => {
      if (a.ts === null && b.ts === null) return 0;
      if (a.ts === null) return -1;
      if (b.ts === null) return 1;
      return a.ts - b.ts;
    });
    return eventos;
  };

  // Função para identificar o tipo do operador (CORRETOR ou ATENDENTE)
  const getTipoOperador = (operadorStr) => {
    if (!operadorStr) return null;
    
    // Extrair código do operador (ex: "PA0004" de "PA0004 PECOS TEIXEIRA")
    const partes = operadorStr.trim().split(" ");
    const codigoOperador = partes[0];
    
    // Buscar nos operadores carregados
    const operadorEncontrado = operadores.find(op => 
      op.codigo_usuario === codigoOperador ||
      op.nome?.toUpperCase().includes(operadorStr.toUpperCase()) ||
      operadorStr.toUpperCase().includes(op.nome?.toUpperCase())
    );
    
    if (operadorEncontrado) {
      // Verificar pelo perfil ou funcao_codigo
      const perfil = operadorEncontrado.perfil?.toUpperCase();
      const funcao = operadorEncontrado.funcao_codigo?.toUpperCase();
      
      if (perfil === "CORRETOR" || funcao === "CORR") {
        return "CORRETOR";
      }
      if (perfil === "ATENDENTE" || funcao === "ATEN" || funcao === "TELE") {
        return "ATENDENTE";
      }
    }
    
    return null; // Tipo não identificado - mostrar todos
  };

  // Calcular bancos com contratos e suas quantidades
  const bancosComContratos = () => {
    const contagem = {};
    vendas.forEach(v => {
      const bancoKey = v.banco_codigo || v.banco_nome;
      if (bancoKey) {
        if (!contagem[bancoKey]) {
          contagem[bancoKey] = {
            codigo: v.banco_codigo,
            nome: v.banco_nome,
            quantidade: 0
          };
        }
        contagem[bancoKey].quantidade++;
      }
    });
    return Object.values(contagem).sort((a, b) => b.quantidade - a.quantidade);
  };

  // Calcular operadores com contratos - APENAS operadores validos (ATENDENTE/CORRETOR)
  const operadoresComContratos = () => {
    // Filtrar apenas operadores validos (perfil ATENDENTE ou CORRETOR)
    const operadoresValidos = operadores.filter(op => 
      op.perfil && ['ATENDENTE', 'CORRETOR'].includes(op.perfil.toUpperCase())
    );
    
    // Para cada operador valido, contar quantos contratos tem
    return operadoresValidos.map(op => {
      const nomeCompleto = op.codigo_usuario ? `${op.codigo_usuario} ${op.nome}` : op.nome;
      const quantidade = vendas.filter(v => {
        const vendedor = v.vendedor || v.operador_comissao || '';
        return vendedor === nomeCompleto || 
               vendedor.includes(op.nome) || 
               (op.codigo_usuario && vendedor.startsWith(op.codigo_usuario));
      }).length;
      return { nome: nomeCompleto, quantidade };
    }).sort((a, b) => b.quantidade - a.quantidade);
  };

  // Calcular correspondentes com contratos e suas quantidades
  const correspondentesComContratos = () => {
    const contagem = {};
    vendas.forEach(v => {
      const correspondente = v.correspondente;
      if (correspondente) {
        if (!contagem[correspondente]) {
          contagem[correspondente] = {
            nome: correspondente,
            quantidade: 0
          };
        }
        contagem[correspondente].quantidade++;
      }
    });
    return Object.values(contagem).sort((a, b) => b.quantidade - a.quantidade);
  };

  // Calcular tipos de operação com contratos e suas quantidades
  const tiposOperacaoComContratos = () => {
    const contagem = {};
    vendas.forEach(v => {
      const tipo = v.tipo_operacao;
      if (tipo) {
        if (!contagem[tipo]) {
          const tipoLabel = tiposOperacao.find(t => t.value === tipo)?.label || tipo;
          contagem[tipo] = {
            value: tipo,
            label: tipoLabel,
            quantidade: 0
          };
        }
        contagem[tipo].quantidade++;
      }
    });
    return Object.values(contagem).sort((a, b) => b.quantidade - a.quantidade);
  };

  // Calcular status com contratos e suas quantidades
  const statusComContratos = () => {
    // Tags de Em Produção
    const tagsProd = ["FALTA_DIGITAR", "FALTA_FORMALIZAR", "DESBLOQUEIO_INSS_60", "DESBLOQUEIO_INSS_90"];
    // Tags de Fluxo Portabilidade
    const tagsPort = ["ENVIADO_CIP_SALDO_DEVEDOR", "ACEITE_CIP_REFIN_PORTABILIDADE", "AGUARDA_MARGEM_REDU"];
    
    const temTag = (v, tags) => {
      const nomes = v.tags_gestao_nomes || [];
      return nomes.some(t => tags.some(tp => (t || "").trim().toUpperCase().includes(tp)));
    };
    
    const contagem = {
      PRODUCAO: { value: "PRODUCAO", label: "Em Produção", quantidade: 0 },
      FLUXO_PORTABILIDADE: { value: "FLUXO_PORTABILIDADE", label: "Fluxo Portabilidade", quantidade: 0 },
      ANALISE: { value: "ANALISE", label: "Em Análise", quantidade: 0 },
      APROVADO: { value: "APROVADO", label: "Aprovado", quantidade: 0 },
      REPROVADO: { value: "REPROVADO", label: "Reprovado", quantidade: 0 },
      RECONSIDERADO: { value: "RECONSIDERADO", label: "Reconsiderado", quantidade: 0 },
      ESTORNADO: { value: "ESTORNADO", label: "Estornado", quantidade: 0 }
    };
    
    vendas.forEach(v => {
      const status = getStatusFromEsteira(v);
      if (contagem[status]) {
        contagem[status].quantidade++;
      }
    });
    
    return Object.values(contagem);
  };

  const filtrarVendas = () => {
    let resultado = vendas;
    
    // Se está buscando por CPF (apenas números, 6+ dígitos), ignora todos os outros filtros
    const isBuscaCPF = searchTerm && searchTerm.replace(/\D/g, '').length >= 6;
    
    if (searchTerm) {
      const searchClean = searchTerm.replace(/\D/g, '').toLowerCase();
      const searchLower = searchTerm.toLowerCase();
      resultado = resultado.filter(v => 
        v.cliente_nome?.toLowerCase().includes(searchLower) ||
        v.cliente_cpf?.replace(/\D/g, '').includes(searchClean) ||
        v.numero_contrato?.toLowerCase().includes(searchLower) ||
        v.id_externo_proposta?.toLowerCase().includes(searchLower) ||
        v.codigo?.toLowerCase().includes(searchLower)
      );
      
      // BUSCA GLOBAL: mostra todos os resultados; aplica Status só se um status ESPECÍFICO estiver selecionado (não os modos de visão "ativos"/"todos")
      if (filtroStatus !== "ativos" && filtroStatus !== "todos") {
        resultado = resultado.filter(v => getStatusFromEsteira(v) === filtroStatus);
      }
      return resultado;
    }
    
    if (filtroStatus === "ativos") {
      // Mostrar apenas Em Produção, Fluxo Portabilidade e Em Análise
      resultado = resultado.filter(v => ["PRODUCAO", "FLUXO_PORTABILIDADE", "ANALISE"].includes(getStatusFromEsteira(v)));
    } else if (filtroStatus !== "todos") {
      resultado = resultado.filter(v => getStatusFromEsteira(v) === filtroStatus);
    }
    
    if (filtroTipo !== "todos") {
      resultado = resultado.filter(v => v.tipo_operacao === filtroTipo);
    }
    
    // Filtro por Banco
    if (filtroBanco !== "todos") {
      resultado = resultado.filter(v => v.banco_codigo === filtroBanco || v.banco_nome === filtroBanco);
    }
    
    // Filtro por Operador
    if (filtroOperador !== "todos") {
      resultado = resultado.filter(v => {
        const vendedor = v.vendedor || v.operador_comissao || '';
        return vendedor === filtroOperador || 
               vendedor.includes(filtroOperador.split(' ').slice(1).join(' ')) ||
               (filtroOperador.includes(' ') && vendedor.startsWith(filtroOperador.split(' ')[0]));
      });
    }
    
    // Filtro por Correspondente
    if (filtroCorrespondente !== "todos") {
      resultado = resultado.filter(v => v.correspondente === filtroCorrespondente);
    }
    
    // Filtro por Tag de Gestão
    if (filtroTagVendas === "PENDENTES") {
      resultado = resultado.filter(isPendenteReal);
    } else if (filtroTagVendas !== "TODAS") {
      resultado = resultado.filter(v => (v.tags_gestao || []).includes(filtroTagVendas));
    }
    
    return resultado;
  };

  // ==================== PAGINACAO + PERMISSAO ====================
  const perfilLogado = (usuarioLogado?.perfil || "").toUpperCase();
  const isGestao = ["MASTER", "MASTER_GERAL", "ADMIN", "ADMIN_EMPRESA", "GERENTE", "GESTOR_ADMINISTRATIVO", "GESTOR_OPERACIONAL"].includes(perfilLogado);
  const PER_PAGE_VENDAS = 20;
  const vendasFiltradas = filtrarVendas();
  const totalPaginasVendas = Math.max(1, Math.ceil(vendasFiltradas.length / PER_PAGE_VENDAS));
  const paginaSeguraVendas = Math.min(paginaAtual, totalPaginasVendas);
  const vendasPagina = vendasFiltradas.slice((paginaSeguraVendas - 1) * PER_PAGE_VENDAS, paginaSeguraVendas * PER_PAGE_VENDAS);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Vendas</h1>
          <p className="text-gray-500 mt-1">Gestão de propostas e vendas</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={periodoFiltro} onValueChange={setPeriodoFiltro}>
            <SelectTrigger className="w-[170px]" data-testid="filter-periodo">
              <SelectValue placeholder="Período" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mes">Mês Atual</SelectItem>
              <SelectItem value="mes_anterior">Mês Anterior</SelectItem>
              {isGestao && <SelectItem value="todos">Todo Período</SelectItem>}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="propostas" className="gap-2">
            <FileText className="h-4 w-4" />
            Em Produção
          </TabsTrigger>
          <TabsTrigger value="pipeline" className="gap-2">
            <ArrowRight className="h-4 w-4" />
            Status
          </TabsTrigger>
        </TabsList>

        {/* Tab: Propostas */}
        <TabsContent value="propostas" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Lista de Propostas</CardTitle>
                <CardDescription>Todas as propostas de venda</CardDescription>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Buscar por Nº Contrato ou CPF..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 w-[250px]"
                    data-testid="vendas-busca"
                  />
                </div>
                <Select value={filtroStatus} onValueChange={setFiltroStatus}>
                  <SelectTrigger className="w-[180px]" data-testid="filter-status">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ativos">
                      Ativos ({vendas.filter(v => ["PRODUCAO", "FLUXO_PORTABILIDADE", "ANALISE"].includes(getStatusFromEsteira(v))).length})
                    </SelectItem>
                    {isGestao && <SelectItem value="todos">Todos Status ({vendas.length})</SelectItem>}
                    {statusComContratos().map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label} ({s.quantidade})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filtroBanco} onValueChange={setFiltroBanco}>
                  <SelectTrigger className="w-[200px]" data-testid="filter-banco">
                    <SelectValue placeholder="Banco" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos Bancos ({vendas.length})</SelectItem>
                    {bancosComContratos().map((banco) => (
                      <SelectItem key={banco.codigo || banco.nome} value={banco.codigo || banco.nome}>
                        {banco.nome} ({banco.quantidade})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filtroCorrespondente} onValueChange={setFiltroCorrespondente}>
                  <SelectTrigger className="w-[200px]" data-testid="filter-correspondente">
                    <SelectValue placeholder="Correspondente" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos Correspondentes ({vendas.length})</SelectItem>
                    {correspondentesComContratos().map((corr) => (
                      <SelectItem key={corr.nome} value={corr.nome}>
                        {corr.nome} ({corr.quantidade})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filtroTipo} onValueChange={setFiltroTipo}>
                  <SelectTrigger className="w-[200px]" data-testid="filter-tipo-operacao">
                    <SelectValue placeholder="Tipo Operação" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos os Tipos ({vendas.length})</SelectItem>
                    {tiposOperacaoComContratos().map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label} ({t.quantidade})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filtroOperador} onValueChange={setFiltroOperador}>
                  <SelectTrigger className="w-[200px]" data-testid="filter-operador">
                    <SelectValue placeholder="Operador" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos Operadores ({vendas.length})</SelectItem>
                    {operadoresComContratos().map((op) => (
                      <SelectItem key={op.nome} value={op.nome}>
                        {op.nome} ({op.quantidade})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            
            {/* Filtros de Tags */}
            <div className="px-6 pb-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex flex-wrap gap-2 items-center">
                  <Badge 
                    variant={filtroTagVendas === "PENDENTES" ? "default" : "outline"}
                    className="cursor-pointer hover:opacity-80 transition-opacity bg-amber-500 text-white border-amber-500"
                    style={{
                      backgroundColor: filtroTagVendas === "PENDENTES" ? "#F59E0B" : "#FFFBEB",
                      color: filtroTagVendas === "PENDENTES" ? "white" : "#D97706",
                      borderColor: "#F59E0B"
                    }}
                    onClick={() => setFiltroTagVendas("PENDENTES")}
                  >
                    Pendentes ({vendas.filter(isPendenteReal).length})
                  </Badge>
                  <Badge 
                    variant={filtroTagVendas === "TODAS" ? "default" : "outline"}
                    className="cursor-pointer hover:bg-slate-100"
                    onClick={() => setFiltroTagVendas("TODAS")}
                  >
                    Todos ({vendas.length})
                  </Badge>
                  {tagsContratos.map(tag => (
                    <Badge 
                      key={tag.id}
                      variant={filtroTagVendas === tag.id ? "default" : "outline"}
                      className="cursor-pointer hover:opacity-80 transition-opacity"
                      style={{ 
                        backgroundColor: filtroTagVendas === tag.id ? tag.cor : tag.bgCor,
                        color: filtroTagVendas === tag.id ? "white" : tag.cor,
                        borderColor: tag.cor
                      }}
                      onClick={() => setFiltroTagVendas(filtroTagVendas === tag.id ? "TODAS" : tag.id)}
                    >
                      {tag.nome} ({contadorPorTagVendas[tag.id] || 0})
                    </Badge>
                  ))}
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowConfigTagsVendas(true)}
                  data-testid="btn-configurar-tags-vendas"
                >
                  <Settings className="w-4 h-4 mr-1" />
                  Configurar Tags
                </Button>
              </div>
            </div>
            
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nº Contrato</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Banco/Tipo</TableHead>
                    <TableHead>Valores</TableHead>
                    <TableHead className="min-w-[150px]">Comissões</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="min-w-[280px]">Linha do Tempo</TableHead>
                    <TableHead>Operador</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                    <TableHead>Motivo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vendasPagina.map((venda) => (
                    <TableRow key={venda.id}>
                      <TableCell className="font-mono text-sm">
                        {venda.codigo}
                        {/* Tags abaixo do Nº Contrato */}
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button size="sm" variant="outline" className="h-6 gap-1 mt-1 text-[10px] w-full">
                              <Tags className="h-3 w-3" />
                              Tags
                              <ChevronDown className="h-3 w-3" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[250px] p-2" align="start">
                            <div className="space-y-1">
                              <p className="text-xs text-slate-500 px-2 pb-2">Selecione as tags (múltiplas)</p>
                              {tagsContratos.map(tag => {
                                const isSelected = (venda.tags_gestao || []).includes(tag.id);
                                return (
                                  <div
                                    key={tag.id}
                                    className={`flex items-center gap-2 px-3 py-2 rounded cursor-pointer hover:bg-slate-100 transition-colors ${isSelected ? 'bg-slate-50' : ''}`}
                                    onClick={() => toggleTagVenda(venda.id, tag.id, !isSelected)}
                                  >
                                    <Checkbox 
                                      checked={isSelected}
                                      className="pointer-events-none"
                                    />
                                    <div 
                                      className="w-3 h-3 rounded-full"
                                      style={{ backgroundColor: tag.cor }}
                                    />
                                    <span className="text-sm" style={{ color: tag.cor }}>
                                      {tag.nome}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </PopoverContent>
                        </Popover>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm">{venda.cliente_nome}</p>
                          <p className="text-xs text-gray-500">{venda.cliente_cpf}</p>
                          {venda.instituicao && (
                            <p className="text-xs text-blue-600">{venda.instituicao}</p>
                          )}
                          {venda.correspondente && (
                            <p className="text-xs text-purple-600">{venda.correspondente}</p>
                          )}
                          {/* Tag Contrato abaixo da info do cliente */}
                          {venda.tags_gestao && venda.tags_gestao.length > 0 && (
                            <div className="mt-1">
                              <p className="text-[10px] text-gray-400 font-medium">Tag Contrato</p>
                              <div className="flex flex-nowrap gap-1 mt-0.5 overflow-x-auto">
                                {venda.tags_gestao.map(tagId => {
                                  const tag = tagsContratos.find(t => t.id === tagId);
                                  if (!tag) return null;
                                  return (
                                    <Badge 
                                      key={tagId}
                                      className="text-[10px] px-1.5 py-0 whitespace-nowrap shrink-0"
                                      style={{ 
                                        backgroundColor: tag.bgCor,
                                        color: tag.cor,
                                        borderColor: tag.cor
                                      }}
                                    >
                                      {tag.nome}
                                    </Badge>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="min-w-[220px]">
                        <div>
                          <Badge variant="outline" className="text-xs">{venda.banco_nome}</Badge>
                          <p className="text-xs text-gray-500 mt-1">{tiposOperacao.find(t => t.value === venda.tipo_operacao)?.label || venda.tipo_operacao}</p>
                          {venda.produto_nome && (
                            <p className="text-xs text-green-600 mt-0.5">{venda.produto_nome}</p>
                          )}
                        </div>
                      </TableCell>
                      {/* Coluna Valores Financeiros */}
                      <TableCell className="min-w-[140px]">
                        <div className="text-xs space-y-0.5">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Solicitado:</span>
                            <span className="font-medium text-green-600">{formatCurrency(venda.valor_solicitado)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Liberado:</span>
                            <span className="font-medium">{formatCurrency(venda.valor_liberado)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Saldo Dev.:</span>
                            <span className="font-medium text-orange-600">{formatCurrency(venda.saldo_devedor)}</span>
                          </div>
                          <div className="flex justify-between border-t pt-0.5 mt-0.5">
                            <span className="text-gray-500">Parcela:</span>
                            <span className="font-medium">{formatCurrency(venda.valor_parcela)} <span className="text-gray-400">({venda.prazo}x)</span></span>
                          </div>
                        </div>
                      </TableCell>
                      {/* Coluna Comissões */}
                      <TableCell className="min-w-[150px]">
                        {(() => {
                          const comissaoFin = comissoes.find(c => c.contrato_id === venda.id);
                          const valorRecebidoEmpresa = parseFloat((comissaoFin && comissaoFin.valor_recebido) || venda.comissao_recebida_valor || 0);
                          const comissoesCalc = calcularComissoes(venda, tabelasComissao, valorRecebidoEmpresa);
                          
                          if (!comissoesCalc && !comissaoFin) {
                            return (
                              <div className="text-xs text-gray-400 italic">
                                Sem tabela
                              </div>
                            );
                          }
                          
                          // Identificar tipo do operador para mostrar apenas comissão relevante
                          const tipoOperador = getTipoOperador(venda.vendedor);
                          
                          // Verificar se usuário logado é ATENDENTE ou CORRETOR
                          const perfilLogado = (usuarioLogado?.perfil || "").toUpperCase();
                          const nomeLogado = usuarioLogado?.codigo_usuario 
                            ? `${usuarioLogado.codigo_usuario} ${usuarioLogado.nome}`.trim().toUpperCase()
                            : (usuarioLogado?.nome || "").toUpperCase();
                          const nomeOperador = (venda.vendedor || venda.operador_comissao || "").toUpperCase();
                          const isProprioContrato = nomeOperador.includes(nomeLogado) || nomeLogado.includes(nomeOperador.split(" ")[0]);
                          const isRestrito = perfilLogado === "ATENDENTE" || perfilLogado === "CORRETOR";
                          const isAtendente = perfilLogado === "ATENDENTE";
                          const isCorretor = perfilLogado === "CORRETOR";
                          const isAdmin = ["MASTER", "MASTER_GERAL", "ADMIN", "ADMIN_EMPRESA", "GERENTE"].includes(perfilLogado);
                          
                          const mostrarComissaoIndividual = !isRestrito || isProprioContrato;
                          
                          const canSeeEmpresa = isAdmin;
                          const canSeeAtendente = isAdmin || isAtendente;
                          const canSeeCorretor = isAdmin || isCorretor;
                          const canSeeGerAdm = isAdmin;
                          const canSeeGerOper = isAdmin;
                          const canSeeTeleVenda = isAdmin;
                          
                          // Se tem tabela, usa calcularComissoes; senão usa dados financeiros
                          if (comissoesCalc) {
                            return (
                              <div className="text-xs space-y-1">
                                {canSeeEmpresa && (
                                  <div>
                                    <span className="text-blue-600 font-medium">Empresa:</span>
                                    <div className="font-semibold text-blue-700">
                                      {comissoesCalc.empresa.percentual}% = {formatCurrency(comissoesCalc.empresa.valor)}
                                    </div>
                                  </div>
                                )}
                                {mostrarComissaoIndividual && (
                                  <>
                                    {canSeeTeleVenda && comissoesCalc.tele_venda.percentual > 0 && (
                                      <div>
                                        <span className="text-green-600">Tele-Venda:</span>
                                        <div className="text-green-700">
                                          {comissoesCalc.tele_venda.percentual}% = {formatCurrency(comissoesCalc.tele_venda.valor)}
                                        </div>
                                      </div>
                                    )}
                                    {canSeeCorretor && comissoesCalc.corretor.percentual > 0 && (tipoOperador === "CORRETOR" || tipoOperador === null) && tipoOperador !== "ATENDENTE" && (
                                      <div>
                                        <span className="text-yellow-600">Corretor:</span>
                                        <div className="text-yellow-700">
                                          {comissoesCalc.corretor.percentual}% = {formatCurrency(comissoesCalc.corretor.valor)}
                                        </div>
                                      </div>
                                    )}
                                    {canSeeAtendente && comissoesCalc.atendente.percentual > 0 && (tipoOperador === "ATENDENTE" || tipoOperador === null) && tipoOperador !== "CORRETOR" && (
                                      <div>
                                        <span className="text-orange-600">Atendente:</span>
                                        <div className="text-orange-700">
                                          {comissoesCalc.atendente.percentual}% = {formatCurrency(comissoesCalc.atendente.valor)}
                                        </div>
                                      </div>
                                    )}
                                    {canSeeGerAdm && comissoesCalc.gerente_adm.percentual > 0 && (
                                      <div>
                                        <span className="text-purple-600">Ger.Adm:</span>
                                        <div className="text-purple-700">
                                          {comissoesCalc.gerente_adm.percentual}% = {formatCurrency(comissoesCalc.gerente_adm.valor)}
                                        </div>
                                      </div>
                                    )}
                                    {canSeeGerOper && comissoesCalc.gerente_oper.percentual > 0 && (
                                      <div>
                                        <span className="text-pink-600">Ger.Oper:</span>
                                        <div className="text-pink-700">
                                          {comissoesCalc.gerente_oper.percentual}% = {formatCurrency(comissoesCalc.gerente_oper.valor)}
                                        </div>
                                      </div>
                                    )}
                                  </>
                                )}
                                {!mostrarComissaoIndividual && (
                                  <div className="text-gray-400 italic text-[10px]">
                                    Comissões do operador
                                  </div>
                                )}
                              </div>
                            );
                          }
                          
                          // Fallback: usar dados da comissão financeira
                          return (
                            <div className="text-xs space-y-1">
                              {canSeeEmpresa && (comissaoFin.valor_empresa > 0 || comissaoFin.valor > 0) && (
                                <div>
                                  <span className="text-blue-600 font-medium">Empresa:</span>
                                  <div className="font-semibold text-blue-700">
                                    {comissaoFin.percentual_empresa || 0}% = {formatCurrency(comissaoFin.valor_empresa || comissaoFin.valor)}
                                  </div>
                                </div>
                              )}
                              {mostrarComissaoIndividual && (
                                <>
                                  {canSeeCorretor && comissaoFin.valor_corretor > 0 && (
                                    <div>
                                      <span className="text-yellow-600">Corretor:</span>
                                      <div className="text-yellow-700">
                                        {comissaoFin.percentual_corretor || 0}% = {formatCurrency(comissaoFin.valor_corretor)}
                                      </div>
                                    </div>
                                  )}
                                  {canSeeAtendente && comissaoFin.valor_atendente > 0 && (
                                    <div>
                                      <span className="text-orange-600">Atendente:</span>
                                      <div className="text-orange-700">
                                        {comissaoFin.percentual_atendente || 0}% = {formatCurrency(comissaoFin.valor_atendente)}
                                      </div>
                                    </div>
                                  )}
                                  {canSeeGerAdm && comissaoFin.valor_ger_adm > 0 && (
                                    <div>
                                      <span className="text-purple-600">Ger.Adm:</span>
                                      <div className="text-purple-700">
                                        {comissaoFin.percentual_ger_adm || 0}% = {formatCurrency(comissaoFin.valor_ger_adm)}
                                      </div>
                                    </div>
                                  )}
                                  {canSeeGerOper && comissaoFin.valor_ger_oper > 0 && (
                                    <div>
                                      <span className="text-pink-600">Ger.Oper:</span>
                                      <div className="text-pink-700">
                                        {comissaoFin.percentual_ger_oper || 0}% = {formatCurrency(comissaoFin.valor_ger_oper)}
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                              {!mostrarComissaoIndividual && (
                                <div className="text-gray-400 italic text-[10px]">
                                  Comissões do operador
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </TableCell>
                      <TableCell>{getStatusBadge(venda)}</TableCell>
                      {/* Coluna Linha do Tempo */}
                      <TableCell className="min-w-[280px]">
                        <div className="flex flex-col gap-1 text-xs">
                          {construirTimeline(venda).map((ev, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full ${ev.dot}`}></div>
                              <span className={ev.textCls}>{ev.label}: {ev.dateVal ? formatDateTime(ev.dateVal) : '-'}</span>
                            </div>
                          ))}
                        </div>
                      </TableCell>
                      {/* Coluna Operador */}
                      <TableCell>
                        <div className="text-xs">
                          <p className="font-medium text-gray-700">
                            {(() => {
                              // Formatar: "PA0004 PECOS" (código + primeiro nome)
                              const operador = venda.vendedor || "";
                              if (!operador) return "-";
                              const partes = operador.trim().split(" ");
                              if (partes.length >= 2) {
                                return `${partes[0]} ${partes[1]}`; // Código + Primeiro nome
                              }
                              return operador;
                            })()}
                          </p>
                          <p className="text-gray-400">{venda.filial || '-'}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => {
                            setVendaSelecionada(venda);
                            setShowDetalhes(true);
                          }}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          {/* Botão Editar Contrato */}
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => navigate(`/cadastro-contrato/${venda.id}/editar`)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {/* Botões baseados no status real da esteira */}
                          {(() => {
                            const statusAtual = getStatusFromEsteira(venda);
                            const comissao = comissoes.find(c => c.contrato_id === venda.id);
                            const isPago = comissao && comissao.status === 'PAGO';
                            
                            return (
                              <>
                                {(statusAtual === "PRODUCAO" || statusAtual === "ANALISE" || statusAtual === "FLUXO_PORTABILIDADE") && (
                                  <>
                                    <Button size="sm" variant="outline" className="h-8 gap-1 text-green-600" onClick={() => atualizarStatus(venda.id, "APROVADO")}>
                                      <CheckCircle className="h-3 w-3" />
                                      Aprovar
                                    </Button>
                                    <Button size="sm" variant="outline" className="h-8 gap-1 text-red-600" onClick={() => abrirModalReprovacao(venda)}>
                                      <XCircle className="h-3 w-3" />
                                      Reprovar
                                    </Button>
                                  </>
                                )}
                                {/* Botão Reconsiderar - aparece quando reprovado */}
                                {statusAtual === "REPROVADO" && (
                                  <Button size="sm" variant="outline" className="h-8 gap-1 text-pink-600 border-pink-300 hover:bg-pink-50" onClick={() => abrirModalReconsideracao(venda)}>
                                    <RefreshCcw className="h-3 w-3" />
                                    Reconsiderar
                                  </Button>
                                )}
                                {/* Botão Estornar - só aparece quando comissão está PAGA */}
                                {isPago && statusAtual !== "ESTORNADO" && (
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="h-8 gap-1 text-orange-600 border-orange-300 hover:bg-orange-50"
                                    onClick={() => abrirModalEstorno(venda)}
                                  >
                                    <RotateCcw className="h-3 w-3" />
                                    Estornar
                                  </Button>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </TableCell>
                      {/* Coluna Motivo - após Ações */}
                      <TableCell className="max-w-[150px]">
                        {venda.motivo ? (
                          <p className="text-xs text-gray-600 truncate" title={venda.motivo}>
                            {venda.motivo}
                          </p>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {/* Paginação (20 por página) */}
              <div className="flex items-center justify-between px-2 py-4 border-t mt-2" data-testid="vendas-paginacao">
                <p className="text-sm text-gray-500">
                  Mostrando {vendasFiltradas.length === 0 ? 0 : (paginaSeguraVendas - 1) * PER_PAGE_VENDAS + 1}
                  {" - "}
                  {Math.min(paginaSeguraVendas * PER_PAGE_VENDAS, vendasFiltradas.length)} de {vendasFiltradas.length}
                </p>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" disabled={paginaSeguraVendas <= 1} onClick={() => setPaginaAtual(p => Math.max(1, p - 1))} data-testid="vendas-pagina-anterior">
                    Anterior
                  </Button>
                  <span className="text-sm text-gray-600">Página {paginaSeguraVendas} de {totalPaginasVendas}</span>
                  <Button variant="outline" size="sm" disabled={paginaSeguraVendas >= totalPaginasVendas} onClick={() => setPaginaAtual(p => Math.min(totalPaginasVendas, p + 1))} data-testid="vendas-pagina-proxima">
                    Próxima
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Status */}
        <TabsContent value="pipeline" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-6">
            {["PRODUCAO", "ANALISE", "APROVADO", "REPROVADO", "RECONSIDERADO", "ESTORNADO"].map((status) => {
              const config = statusConfig[status];
              const vendasStatus = vendas.filter(v => getStatusFromEsteira(v) === status);
              return (
                <Card key={status} className="min-h-[400px]">
                  <CardHeader className={`${config.color} rounded-t-lg py-3`}>
                    <CardTitle className="text-sm flex items-center gap-2">
                      {config.label}
                      <Badge variant="secondary" className="ml-auto">{vendasStatus.length}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-3">
                    <ScrollArea className="h-[350px]">
                      <div className="space-y-2">
                        {vendasStatus.map((venda) => (
                          <div key={venda.id} className="p-3 rounded-lg border bg-white hover:shadow-md cursor-pointer transition-shadow" onClick={() => { setVendaSelecionada(venda); setShowDetalhes(true); }}>
                            <p className="font-medium text-sm truncate">{venda.cliente_nome}</p>
                            <p className="text-xs text-gray-500">{venda.banco_nome}</p>
                            {/* Tags de Gestão */}
                            {venda.tags_gestao && venda.tags_gestao.length > 0 && (
                              <div className="flex flex-nowrap gap-1 mt-1 overflow-x-auto">
                                {venda.tags_gestao.map(tagId => {
                                  const tag = tagsContratos.find(t => t.id === tagId);
                                  if (!tag) return null;
                                  return (
                                    <Badge 
                                      key={tagId}
                                      className="text-[9px] px-1 py-0 whitespace-nowrap shrink-0"
                                      style={{ 
                                        backgroundColor: tag.bgCor,
                                        color: tag.cor
                                      }}
                                    >
                                      {tag.nome}
                                    </Badge>
                                  );
                                })}
                              </div>
                            )}
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-sm font-semibold text-green-600">{formatCurrency(venda.valor_solicitado)}</span>
                              <span className="text-xs text-gray-400">{formatDate(venda.data_criacao)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialog de Detalhes */}
      <Dialog open={showDetalhes} onOpenChange={setShowDetalhes}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Detalhes da Proposta
            </DialogTitle>
            <DialogDescription>Contrato: {vendaSelecionada?.codigo}</DialogDescription>
          </DialogHeader>
          {vendaSelecionada && (
            <div className="space-y-4">
              {/* Seção: Cliente e Status */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Identificação
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs text-gray-500">Cliente</Label>
                    <p className="font-medium">{vendaSelecionada.cliente_nome}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">CPF</Label>
                    <p className="font-medium">{vendaSelecionada.cliente_cpf || "-"}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Status</Label>
                    <div className="mt-1">{getStatusBadge(vendaSelecionada)}</div>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Operador</Label>
                    <p className="font-medium">{vendaSelecionada.vendedor}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Filial</Label>
                    <p className="font-medium">{vendaSelecionada.filial || "-"}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Segmento</Label>
                    <p className="font-medium">{vendaSelecionada.segmento || "-"}</p>
                  </div>
                </div>
                {/* Tags de Gestão no modal de detalhes */}
                {vendaSelecionada.tags_gestao && vendaSelecionada.tags_gestao.length > 0 && (
                  <div className="mt-3 pt-3 border-t">
                    <Label className="text-xs text-gray-500 block mb-2">Tags de Gestão</Label>
                    <div className="flex flex-wrap gap-1">
                      {vendaSelecionada.tags_gestao.map(tagId => {
                        const tag = tagsContratos.find(t => t.id === tagId);
                        if (!tag) return null;
                        return (
                          <Badge 
                            key={tagId}
                            className="text-xs"
                            style={{ 
                              backgroundColor: tag.bgCor,
                              color: tag.cor,
                              borderColor: tag.cor
                            }}
                          >
                            {tag.nome}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                )}
                {/* Botão Linha do Tempo */}
                <div className="mt-3 pt-3 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-blue-600 border-blue-200 hover:bg-blue-50"
                    onClick={() => {
                      setTimelineContrato(vendaSelecionada);
                      setShowTimelineContrato(true);
                    }}
                    data-testid="btn-timeline-contrato"
                  >
                    <History className="h-4 w-4 mr-2" />
                    Ver Linha do Tempo
                  </Button>
                </div>
              </div>

              {/* Seção: Linha de Financiamento */}
              <div className="p-4 bg-amber-50 rounded-lg">
                <h4 className="text-sm font-semibold text-amber-700 mb-3 flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Linha de Financiamento
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs text-gray-500">Banco</Label>
                    <p className="font-medium">{vendaSelecionada.banco_nome}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Instituição</Label>
                    <p className="font-medium text-sm">{vendaSelecionada.instituicao || "-"}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Tipo Operação</Label>
                    <p className="font-medium">{vendaSelecionada.tipo_operacao || "-"}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Tipo Contrato</Label>
                    <p className="font-medium">{vendaSelecionada.tipo_contrato || "-"}</p>
                  </div>
                </div>
              </div>

              {/* Seção: Valores */}
              <div className="p-4 bg-green-50 rounded-lg">
                <h4 className="text-sm font-semibold text-green-700 mb-3 flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Valores
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs text-gray-500">Valor Solicitado</Label>
                    <p className="font-medium text-green-600">{formatCurrency(vendaSelecionada.valor_solicitado)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Valor Liberado</Label>
                    <p className="font-medium text-green-600">{formatCurrency(vendaSelecionada.valor_liberado)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Saldo Devedor</Label>
                    <p className="font-medium">{formatCurrency(vendaSelecionada.saldo_devedor)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Qtde. Parcelas</Label>
                    <p className="font-medium">{vendaSelecionada.prazo ? `${vendaSelecionada.prazo}x` : "-"}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Valor Parcela</Label>
                    <p className="font-medium">{formatCurrency(vendaSelecionada.valor_parcela)}</p>
                  </div>
                </div>
              </div>

              {/* Seção: Datas */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="text-sm font-semibold text-blue-700 mb-3 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Datas do Contrato
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs text-gray-500">Nº do Contrato</Label>
                    <p className="font-medium font-mono">{vendaSelecionada.codigo}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Data do Contrato</Label>
                    <p className="font-medium">{formatDate(vendaSelecionada.data_contrato)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">1º Vencimento</Label>
                    <p className="font-medium">{formatDate(vendaSelecionada.data_primeiro_vencimento)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Celular Formalização</Label>
                    <p className="font-medium">{vendaSelecionada.celular_formalizacao || "-"}</p>
                  </div>
                </div>
              </div>

              {/* Seção: Dados Bancários */}
              <div className="p-4 bg-purple-50 rounded-lg">
                <h4 className="text-sm font-semibold text-purple-700 mb-3 flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Conta Bancária para Pagamento
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-xs text-gray-500">Banco</Label>
                    <p className="font-medium">{vendaSelecionada.banco_pagamento || "-"}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Forma Pagamento</Label>
                    <p className="font-medium">{vendaSelecionada.forma_pagamento?.replace(/_/g, " ") || "-"}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Agência</Label>
                    <p className="font-medium">{vendaSelecionada.agencia || "-"}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Conta</Label>
                    <p className="font-medium">{vendaSelecionada.conta_corrente || "-"}</p>
                  </div>
                </div>
              </div>

              {/* Seção: Linha do Tempo de Status */}
              <div className="p-4 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg border border-indigo-100">
                <h4 className="text-sm font-semibold text-indigo-700 mb-4 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Linha do Tempo
                </h4>
                
                {/* Timeline Container */}
                <div className="relative">
                  {/* Linha vertical conectora */}
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-indigo-200"></div>
                  
                  {/* Itens da timeline */}
                  <div className="space-y-4">
                    
                    {/* Cadastro - Sempre presente */}
                    <div className="relative flex items-start gap-4">
                      <div className="relative z-10 flex items-center justify-center w-8 h-8 rounded-full bg-blue-500 ring-2 ring-blue-200">
                        <FileText className="h-4 w-4 text-white" />
                      </div>
                      <div className="flex-1 min-w-0 pb-4">
                        <div className="flex items-center justify-between">
                          <p className="font-semibold text-blue-700">Cadastro</p>
                        </div>
                        <p className="text-sm text-gray-600">{formatDateTime(vendaSelecionada.data_criacao)}</p>
                        <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                          <User className="h-3 w-3" />
                          {vendaSelecionada.vendedor}
                        </p>
                      </div>
                    </div>
                    
                    {/* Em Produção - Sempre presente após cadastro */}
                    <div className="relative flex items-start gap-4">
                      <div className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full ${
                        getStatusFromEsteira(vendaSelecionada) === 'PRODUCAO' 
                          ? 'bg-cyan-500 ring-4 ring-cyan-200' 
                          : 'bg-cyan-100 border-2 border-cyan-400'
                      }`}>
                        <Clock className={`h-4 w-4 ${getStatusFromEsteira(vendaSelecionada) === 'PRODUCAO' ? 'text-white' : 'text-cyan-600'}`} />
                      </div>
                      <div className="flex-1 min-w-0 pb-4">
                        <div className="flex items-center justify-between">
                          <p className={`font-semibold ${getStatusFromEsteira(vendaSelecionada) === 'PRODUCAO' ? 'text-cyan-700' : 'text-gray-700'}`}>
                            Em Produção
                            {getStatusFromEsteira(vendaSelecionada) === 'PRODUCAO' && (
                              <Badge className="ml-2 bg-cyan-500 text-white text-xs">Atual</Badge>
                            )}
                          </p>
                        </div>
                        <p className="text-sm text-gray-600">{formatDateTime(vendaSelecionada.data_producao || vendaSelecionada.data_criacao)}</p>
                        <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                          <User className="h-3 w-3" />
                          {vendaSelecionada.vendedor}
                        </p>
                      </div>
                    </div>
                    
                    {/* Em Análise */}
                    {(vendaSelecionada.data_em_analise || vendaSelecionada.esteira_contrato === "EM_ANALISE") && (
                      <div className="relative flex items-start gap-4">
                        <div className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full ${
                          vendaSelecionada.esteira_contrato === 'EM_ANALISE' 
                            ? 'bg-yellow-500 ring-4 ring-yellow-200' 
                            : 'bg-yellow-100 border-2 border-yellow-400'
                        }`}>
                          <Clock className={`h-4 w-4 ${vendaSelecionada.esteira_contrato === 'EM_ANALISE' ? 'text-white' : 'text-yellow-600'}`} />
                        </div>
                        <div className="flex-1 min-w-0 pb-4">
                          <div className="flex items-center justify-between">
                            <p className={`font-semibold ${vendaSelecionada.esteira_contrato === 'EM_ANALISE' ? 'text-yellow-700' : 'text-gray-700'}`}>
                              Em Análise
                              {vendaSelecionada.esteira_contrato === 'EM_ANALISE' && (
                                <Badge className="ml-2 bg-yellow-500 text-white text-xs">Atual</Badge>
                              )}
                            </p>
                          </div>
                          <p className="text-sm text-gray-600">{formatDateTime(vendaSelecionada.data_em_analise)}</p>
                          <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                            <User className="h-3 w-3" />
                            {vendaSelecionada.vendedor}
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {/* Aprovado */}
                    {(vendaSelecionada.data_aprovacao || vendaSelecionada.situacao === 'ATIVO' || vendaSelecionada.esteira_contrato === 'APROVADO') && (
                      <div className="relative flex items-start gap-4">
                        <div className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full ${
                          (vendaSelecionada.situacao === 'ATIVO' || vendaSelecionada.esteira_contrato === 'APROVADO')
                            ? 'bg-green-500 ring-4 ring-green-200' 
                            : 'bg-green-100 border-2 border-green-400'
                        }`}>
                          <CheckCircle className={`h-4 w-4 ${(vendaSelecionada.situacao === 'ATIVO' || vendaSelecionada.esteira_contrato === 'APROVADO') ? 'text-white' : 'text-green-600'}`} />
                        </div>
                        <div className="flex-1 min-w-0 pb-4">
                          <div className="flex items-center justify-between">
                            <p className={`font-semibold ${(vendaSelecionada.situacao === 'ATIVO' || vendaSelecionada.esteira_contrato === 'APROVADO') ? 'text-green-700' : 'text-gray-700'}`}>
                              Aprovado
                              {(vendaSelecionada.situacao === 'ATIVO' || vendaSelecionada.esteira_contrato === 'APROVADO') && (
                                <Badge className="ml-2 bg-green-500 text-white text-xs">Atual</Badge>
                              )}
                            </p>
                          </div>
                          <p className="text-sm text-gray-600">
                            {vendaSelecionada.data_aprovacao ? formatDateTime(vendaSelecionada.data_aprovacao) : '-'}
                          </p>
                          <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                            <User className="h-3 w-3" />
                            {vendaSelecionada.operador_aprovacao || vendaSelecionada.vendedor}
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {/* === FLUXO FINANCEIRO COMPLETO === */}
                    {(() => {
                      const comissao = comissoes.find(c => c.contrato_id === vendaSelecionada.id);
                      const isAprovado = vendaSelecionada.situacao === 'ATIVO' || vendaSelecionada.esteira_contrato === 'APROVADO';
                      const isPendRecebimento = comissao && (comissao.status === 'PENDENTE_RECEBIMENTO' || comissao.status_recebimento === 'PENDENTE');
                      const temRecebimento = comissao && (comissao.status_recebimento === 'RECEBIDO' || comissao.data_recebimento || comissao.status === 'PENDENTE_PAGAMENTO' || comissao.status === 'PAGO' || comissao.status === 'ESTORNADO');
                      const isPendPagamento = comissao && (comissao.status === 'PENDENTE_PAGAMENTO' || (comissao.status_pagamento === 'PENDENTE' && temRecebimento));
                      const temPagamento = comissao && (comissao.status_pagamento === 'PAGO' || comissao.data_pagamento || comissao.status === 'PAGO' || comissao.status === 'ESTORNADO');
                      const isPago = comissao && (comissao.status === 'PAGO' || (comissao.status_pagamento === 'PAGO' && comissao.status !== 'ESTORNADO'));
                      const isEstornado = comissao && comissao.status === 'ESTORNADO';
                      
                      if (!isAprovado && !comissao) return null;
                      
                      return (
                        <>
                          {/* 1. Pend. Comissão Empresa (PENDENTE_RECEBIMENTO) */}
                          {(isAprovado || comissao) && (
                            <div className="relative flex items-start gap-4">
                              <div className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full ${
                                isPendRecebimento
                                  ? 'bg-orange-500 ring-4 ring-orange-200' 
                                  : (temRecebimento || isPago || isEstornado) 
                                    ? 'bg-orange-100 border-2 border-orange-400'
                                    : 'bg-gray-100 border-2 border-gray-300'
                              }`}>
                                <CircleDollarSign className={`h-4 w-4 ${isPendRecebimento ? 'text-white' : (temRecebimento || isPago) ? 'text-orange-600' : 'text-gray-400'}`} />
                              </div>
                              <div className="flex-1 min-w-0 pb-4">
                                <div className="flex items-center justify-between">
                                  <p className={`font-semibold ${isPendRecebimento ? 'text-orange-700' : (temRecebimento || isPago) ? 'text-orange-600' : 'text-gray-500'}`}>
                                    Pend. Comissão Empresa
                                    {isPendRecebimento && (
                                      <Badge className="ml-2 bg-orange-500 text-white text-xs">Atual</Badge>
                                    )}
                                    {temRecebimento && (
                                      <CheckCircle className="inline ml-2 h-4 w-4 text-green-500" />
                                    )}
                                  </p>
                                </div>
                                <p className="text-sm text-gray-600">
                                  {comissao?.data ? formatDateTime(comissao.data) : '-'}
                                </p>
                                {comissao && (
                                  <p className="text-xs text-orange-600 mt-1">
                                    Valor: {formatCurrency(comissao.valor_empresa || comissao.valor)}
                                  </p>
                                )}
                              </div>
                            </div>
                          )}

                          {/* 2. Comissão Recebida Empresa */}
                          {temRecebimento && (
                            <div className="relative flex items-start gap-4">
                              <div className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full ${
                                temRecebimento && !isPendPagamento && !isPago && !isEstornado
                                  ? 'bg-teal-500 ring-4 ring-teal-200'
                                  : 'bg-teal-100 border-2 border-teal-400'
                              }`}>
                                <CheckCircle className={`h-4 w-4 ${temRecebimento && !isPendPagamento && !isPago ? 'text-white' : 'text-teal-600'}`} />
                              </div>
                              <div className="flex-1 min-w-0 pb-4">
                                <div className="flex items-center justify-between">
                                  <p className={`font-semibold ${temRecebimento ? 'text-teal-700' : 'text-gray-500'}`}>
                                    Rec. Empresa
                                    {temRecebimento && !isPendPagamento && !isPago && !isEstornado && (
                                      <Badge className="ml-2 bg-teal-500 text-white text-xs">Atual</Badge>
                                    )}
                                    {(isPendPagamento || isPago || isEstornado) && (
                                      <CheckCircle className="inline ml-2 h-4 w-4 text-green-500" />
                                    )}
                                  </p>
                                </div>
                                <p className="text-sm text-gray-600">
                                  {comissao?.data_recebimento ? formatDateTime(comissao.data_recebimento) : '-'}
                                </p>
                                {comissao?.valor_recebido && (
                                  <p className="text-xs text-teal-600 mt-1">
                                    Recebido: {formatCurrency(comissao.valor_recebido)}
                                  </p>
                                )}
                              </div>
                            </div>
                          )}

                          {/* 3. Pendência Pagamento Equipe */}
                          {temRecebimento && (
                            <div className="relative flex items-start gap-4">
                              <div className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full ${
                                isPendPagamento
                                  ? 'bg-yellow-500 ring-4 ring-yellow-200' 
                                  : (isPago || isEstornado) 
                                    ? 'bg-yellow-100 border-2 border-yellow-400'
                                    : 'bg-gray-100 border-2 border-gray-300'
                              }`}>
                                <Wallet className={`h-4 w-4 ${isPendPagamento ? 'text-white' : (isPago || isEstornado) ? 'text-yellow-600' : 'text-gray-400'}`} />
                              </div>
                              <div className="flex-1 min-w-0 pb-4">
                                <div className="flex items-center justify-between">
                                  <p className={`font-semibold ${isPendPagamento ? 'text-yellow-700' : (isPago || isEstornado) ? 'text-yellow-600' : 'text-gray-500'}`}>
                                    Pend. Pagamento Equipe
                                    {isPendPagamento && (
                                      <Badge className="ml-2 bg-yellow-500 text-white text-xs">Atual</Badge>
                                    )}
                                    {(isPago || isEstornado) && (
                                      <CheckCircle className="inline ml-2 h-4 w-4 text-green-500" />
                                    )}
                                  </p>
                                </div>
                                <p className="text-sm text-gray-600">
                                  {comissao?.data_recebimento ? formatDateTime(comissao.data_recebimento) : '-'}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* 4. PAGO Equipe */}
                          {temPagamento && (
                            <div className="relative flex items-start gap-4">
                              <div className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full ${
                                isPago
                                  ? 'bg-emerald-500 ring-4 ring-emerald-200'
                                  : 'bg-emerald-100 border-2 border-emerald-400'
                              }`}>
                                <Banknote className={`h-4 w-4 ${isPago ? 'text-white' : 'text-emerald-600'}`} />
                              </div>
                              <div className="flex-1 min-w-0 pb-4">
                                <div className="flex items-center justify-between">
                                  <p className={`font-semibold ${isPago ? 'text-emerald-700' : 'text-gray-500'}`}>
                                    Pago Equipe
                                    {isPago && (
                                      <Badge className="ml-2 bg-emerald-500 text-white text-xs">Finalizado</Badge>
                                    )}
                                  </p>
                                </div>
                                <p className="text-sm text-gray-600">
                                  {comissao?.data_pagamento ? formatDateTime(comissao.data_pagamento) : '-'}
                                </p>
                              </div>
                            </div>
                          )}
                        </>
                      );
                    })()}
                    
                    {/* Reprovado */}
                    {(vendaSelecionada.data_reprovacao || vendaSelecionada.situacao === 'REPROVADO' || vendaSelecionada.esteira_contrato === 'REPROVADO') && (
                      <div className="relative flex items-start gap-4">
                        <div className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full ${
                          (vendaSelecionada.situacao === 'REPROVADO' || vendaSelecionada.esteira_contrato === 'REPROVADO')
                            ? 'bg-red-500 ring-4 ring-red-200' 
                            : 'bg-red-100 border-2 border-red-400'
                        }`}>
                          <XCircle className={`h-4 w-4 ${(vendaSelecionada.situacao === 'REPROVADO' || vendaSelecionada.esteira_contrato === 'REPROVADO') ? 'text-white' : 'text-red-600'}`} />
                        </div>
                        <div className="flex-1 min-w-0 pb-4">
                          <div className="flex items-center justify-between">
                            <p className={`font-semibold ${(vendaSelecionada.situacao === 'REPROVADO' || vendaSelecionada.esteira_contrato === 'REPROVADO') ? 'text-red-700' : 'text-gray-700'}`}>
                              Reprovado
                              {(vendaSelecionada.situacao === 'REPROVADO' || vendaSelecionada.esteira_contrato === 'REPROVADO') && (
                                <Badge className="ml-2 bg-red-500 text-white text-xs">Atual</Badge>
                              )}
                            </p>
                          </div>
                          <p className="text-sm text-gray-600">
                            {vendaSelecionada.data_reprovacao ? formatDateTime(vendaSelecionada.data_reprovacao) : '-'}
                          </p>
                          <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                            <User className="h-3 w-3" />
                            {vendaSelecionada.operador_reprovacao || vendaSelecionada.vendedor}
                          </p>
                          {vendaSelecionada.motivo_reprovacao && (
                            <p className="text-xs text-red-600 mt-2 p-2 bg-red-50 rounded">
                              <strong>Motivo:</strong> {vendaSelecionada.motivo_reprovacao}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Reconsiderado */}
                    {(vendaSelecionada.data_reconsideracao || vendaSelecionada.situacao === 'RECONSIDERADO' || vendaSelecionada.esteira_contrato === 'RECONSIDERADO') && (
                      <div className="relative flex items-start gap-4">
                        <div className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full ${
                          (vendaSelecionada.situacao === 'RECONSIDERADO' || vendaSelecionada.esteira_contrato === 'RECONSIDERADO')
                            ? 'bg-pink-500 ring-4 ring-pink-200' 
                            : 'bg-pink-100 border-2 border-pink-400'
                        }`}>
                          <RefreshCcw className={`h-4 w-4 ${(vendaSelecionada.situacao === 'RECONSIDERADO' || vendaSelecionada.esteira_contrato === 'RECONSIDERADO') ? 'text-white' : 'text-pink-600'}`} />
                        </div>
                        <div className="flex-1 min-w-0 pb-4">
                          <div className="flex items-center justify-between">
                            <p className={`font-semibold ${(vendaSelecionada.situacao === 'RECONSIDERADO' || vendaSelecionada.esteira_contrato === 'RECONSIDERADO') ? 'text-pink-700' : 'text-gray-700'}`}>
                              Reconsiderado
                              {(vendaSelecionada.situacao === 'RECONSIDERADO' || vendaSelecionada.esteira_contrato === 'RECONSIDERADO') && (
                                <Badge className="ml-2 bg-pink-500 text-white text-xs">Atual</Badge>
                              )}
                            </p>
                          </div>
                          <p className="text-sm text-gray-600">
                            {vendaSelecionada.data_reconsideracao ? formatDateTime(vendaSelecionada.data_reconsideracao) : '-'}
                          </p>
                          <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                            <User className="h-3 w-3" />
                            {vendaSelecionada.operador_reconsideracao || vendaSelecionada.vendedor}
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {/* Estornado - De Todos Envolvidos */}
                    {(vendaSelecionada.data_estorno || vendaSelecionada.situacao === 'ESTORNADO' || vendaSelecionada.esteira_contrato === 'ESTORNADO') && (
                      <div className="relative flex items-start gap-4">
                        <div className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full ${
                          (vendaSelecionada.situacao === 'ESTORNADO' || vendaSelecionada.esteira_contrato === 'ESTORNADO')
                            ? 'bg-red-600 ring-4 ring-red-200' 
                            : 'bg-orange-100 border-2 border-orange-400'
                        }`}>
                          <XCircle className={`h-4 w-4 ${(vendaSelecionada.situacao === 'ESTORNADO' || vendaSelecionada.esteira_contrato === 'ESTORNADO') ? 'text-white' : 'text-orange-600'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className={`font-semibold ${(vendaSelecionada.situacao === 'ESTORNADO' || vendaSelecionada.esteira_contrato === 'ESTORNADO') ? 'text-red-700' : 'text-gray-600'}`}>
                              Estornado
                              {(vendaSelecionada.situacao === 'ESTORNADO' || vendaSelecionada.esteira_contrato === 'ESTORNADO') && (
                                <Badge className="ml-2 bg-red-600 text-white text-xs">Atual</Badge>
                              )}
                            </p>
                          </div>
                          <p className="text-sm text-gray-600">
                            {vendaSelecionada.data_estorno ? formatDateTime(vendaSelecionada.data_estorno) : '-'}
                          </p>
                          <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                            <User className="h-3 w-3" />
                            {vendaSelecionada.operador_estorno || vendaSelecionada.vendedor}
                          </p>
                          {vendaSelecionada.motivo_estorno && (
                            <p className="text-xs text-red-600 mt-2 p-2 bg-red-50 rounded">
                              <strong>Motivo:</strong> {vendaSelecionada.motivo_estorno}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    
                  </div>
                </div>
              </div>

              {/* Seção: Observações */}
              {(vendaSelecionada.observacoes || vendaSelecionada.observacoes_relatorio) && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Observações / Histórico</h4>
                  {vendaSelecionada.observacoes && (
                    <div className="space-y-1">
                      {vendaSelecionada.observacoes.split("\n").map((linha, idx) => (
                        <p key={idx} className={`text-sm ${
                          linha.includes("[REPROVADO") ? "text-red-600 bg-red-50 p-2 rounded font-medium" :
                          linha.includes("[RECONSIDERADO") ? "text-pink-600 bg-pink-50 p-2 rounded font-medium" :
                          linha.includes("[ESTORNADO") ? "text-orange-600 bg-orange-50 p-2 rounded font-medium" :
                          "text-gray-600"
                        }`}>
                          {linha}
                        </p>
                      ))}
                    </div>
                  )}
                  {vendaSelecionada.observacoes_relatorio && (
                    <p className="text-sm text-gray-500 italic mt-2">{vendaSelecionada.observacoes_relatorio}</p>
                  )}
                </div>
              )}

              <Separator />
              
              {/* Ações */}
              <div className="space-y-3">
                {/* Botões de Editar e Excluir */}
                <div className="flex gap-2">
                  {/* Editar - disponível para Em Análise, Aprovado, Reprovado */}
                  {["ANALISE", "APROVADO", "REPROVADO"].includes(vendaSelecionada.status) && (
                    <Link to={`/cadastro-contrato/${vendaSelecionada.id}/editar`} className="flex-1">
                      <Button variant="outline" className="w-full">
                        <Pencil className="h-4 w-4 mr-2" />
                        Editar Contrato
                      </Button>
                    </Link>
                  )}
                  
                </div>
                
                {/* Nota sobre exclusão para contratos protegidos */}
                {["ATIVO", "APROVADO", "REPROVADO", "ESTORNADO"].includes(vendaSelecionada.situacao) && (
                  <p className="text-xs text-gray-400 italic text-center">
                    Contratos aprovados, reprovados ou estornados não podem ser excluídos pois fazem parte do histórico do cliente.
                  </p>
                )}
                
                {/* Ações de mudança de status */}
                <div className="flex gap-2 flex-wrap">
                  {vendaSelecionada.status === "PROPOSTA" && (
                    <Button className="flex-1" onClick={() => { atualizarStatus(vendaSelecionada.id, "ANALISE"); setShowDetalhes(false); }}>
                      <Send className="h-4 w-4 mr-2" />
                      Enviar para Análise
                    </Button>
                  )}
                  {(getStatusFromEsteira(vendaSelecionada) === "ANALISE" || getStatusFromEsteira(vendaSelecionada) === "PRODUCAO" || getStatusFromEsteira(vendaSelecionada) === "FLUXO_PORTABILIDADE") && (
                    <>
                      <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={() => { atualizarStatus(vendaSelecionada.id, "APROVADO"); setShowDetalhes(false); }}>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Aprovar
                      </Button>
                      <Button variant="destructive" className="flex-1" onClick={() => { setShowDetalhes(false); abrirModalReprovacao(vendaSelecionada); }}>
                        <XCircle className="h-4 w-4 mr-2" />
                        Reprovar
                      </Button>
                    </>
                  )}
                  {getStatusFromEsteira(vendaSelecionada) === "REPROVADO" && (
                    <Button className="flex-1 bg-pink-600 hover:bg-pink-700" onClick={() => { setShowDetalhes(false); abrirModalReconsideracao(vendaSelecionada); }}>
                      <RefreshCcw className="h-4 w-4 mr-2" />
                      Reconsiderar
                    </Button>
                  )}
                  {vendaSelecionada.status === "APROVADO" && (
                    <>
                      <Button className="flex-1 bg-purple-600 hover:bg-purple-700" onClick={() => { atualizarStatus(vendaSelecionada.id, "FORMALIZADO"); setShowDetalhes(false); }}>
                        <FileText className="h-4 w-4 mr-2" />
                        Formalizar
                      </Button>
                      <Button variant="outline" className="flex-1" onClick={() => { setShowDetalhes(false); abrirModalEstorno(vendaSelecionada); }}>
                        <XCircle className="h-4 w-4 mr-2" />
                        Estornar
                      </Button>
                    </>
                  )}
                  {vendaSelecionada.status === "FORMALIZADO" && (
                    <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => { atualizarStatus(vendaSelecionada.id, "PAGO"); setShowDetalhes(false); }}>
                      <DollarSign className="h-4 w-4 mr-2" />
                      Marcar como Pago
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Reprovação */}
      <Dialog open={showReprovacao} onOpenChange={setShowReprovacao}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="h-5 w-5" />
              Reprovar Contrato
            </DialogTitle>
            <DialogDescription>
              Informe o motivo da reprovação do contrato
            </DialogDescription>
          </DialogHeader>
          
          {contratoParaReprovar && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium">{contratoParaReprovar.cliente_nome}</p>
                <p className="text-sm text-gray-500">Contrato: {contratoParaReprovar.codigo}</p>
                <p className="text-sm text-gray-500">Valor: {formatCurrency(contratoParaReprovar.valor_solicitado)}</p>
              </div>
              
              <div>
                <Label htmlFor="motivo" className="text-sm font-semibold">Motivo da Reprovação *</Label>
                <Textarea
                  id="motivo"
                  placeholder="Descreva o motivo da reprovação..."
                  value={motivoReprovacao}
                  onChange={(e) => setMotivoReprovacao(e.target.value)}
                  className="mt-2 min-h-[100px]"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReprovacao(false)}>
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmarReprovacao}
              disabled={!motivoReprovacao.trim()}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Confirmar Reprovação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Reconsideração */}
      <Dialog open={showReconsideracao} onOpenChange={setShowReconsideracao}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-pink-600">
              <RefreshCcw className="h-5 w-5" />
              Reconsiderar Contrato
            </DialogTitle>
            <DialogDescription>
              O contrato voltará para Em Análise. Informe o motivo da reconsideração.
            </DialogDescription>
          </DialogHeader>
          
          {contratoParaReconsiderar && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium">{contratoParaReconsiderar.cliente_nome}</p>
                <p className="text-sm text-gray-500">Contrato: {contratoParaReconsiderar.codigo}</p>
                <p className="text-sm text-gray-500">Valor: {formatCurrency(contratoParaReconsiderar.valor_solicitado)}</p>
              </div>
              
              {contratoParaReconsiderar.motivo_reprovacao && (
                <div className="p-3 bg-red-50 rounded-lg">
                  <p className="text-xs font-semibold text-red-600 mb-1">Motivo da Reprovação anterior:</p>
                  <p className="text-sm text-red-700">{contratoParaReconsiderar.motivo_reprovacao}</p>
                </div>
              )}
              
              <div>
                <Label htmlFor="motivo-reconsideracao" className="text-sm font-semibold">Motivo da Reconsideração *</Label>
                <Textarea
                  id="motivo-reconsideracao"
                  placeholder="Descreva o motivo da reconsideração..."
                  value={motivoReconsideracao}
                  onChange={(e) => setMotivoReconsideracao(e.target.value)}
                  className="mt-2 min-h-[100px]"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReconsideracao(false)}>
              Cancelar
            </Button>
            <Button 
              className="bg-pink-600 hover:bg-pink-700"
              onClick={confirmarReconsideracao}
              disabled={!motivoReconsideracao.trim()}
            >
              <RefreshCcw className="h-4 w-4 mr-2" />
              Confirmar Reconsideração
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Estorno */}
      <Dialog open={showEstorno} onOpenChange={setShowEstorno}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-600">
              <XCircle className="h-5 w-5" />
              Estornar Contrato
            </DialogTitle>
            <DialogDescription>
              Informe o motivo do estorno do contrato
            </DialogDescription>
          </DialogHeader>
          
          {contratoParaEstornar && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium">{contratoParaEstornar.cliente_nome}</p>
                <p className="text-sm text-gray-500">Contrato: {contratoParaEstornar.codigo}</p>
                <p className="text-sm text-gray-500">Valor: {formatCurrency(contratoParaEstornar.valor_solicitado)}</p>
              </div>
              
              <div>
                <Label htmlFor="motivo-estorno" className="text-sm font-semibold">Motivo do Estorno *</Label>
                <Textarea
                  id="motivo-estorno"
                  placeholder="Descreva o motivo do estorno..."
                  value={motivoEstorno}
                  onChange={(e) => setMotivoEstorno(e.target.value)}
                  className="mt-2 min-h-[100px]"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEstorno(false)}>
              Cancelar
            </Button>
            <Button 
              variant="secondary" 
              onClick={confirmarEstorno}
              disabled={!motivoEstorno.trim()}
            >
              <XCircle className="h-4 w-4 mr-2" />
              Confirmar Estorno
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Clientes por Banco */}
      <Dialog open={showClientesBanco} onOpenChange={setShowClientesBanco}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-blue-600" />
              Clientes - {bancoSelecionado}
            </DialogTitle>
            <DialogDescription>
              {clientesDoBanco.length} {clientesDoBanco.length === 1 ? 'contrato encontrado' : 'contratos encontrados'}
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-2">
              {clientesDoBanco.map((venda) => (
                <div key={venda.id} className="p-3 rounded-lg border hover:bg-slate-50">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-full">
                        <User className="h-4 w-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium">{venda.cliente_nome}</p>
                        <p className="text-sm text-gray-500">CPF: {venda.cliente_cpf || "-"}</p>
                        <p className="text-xs text-gray-400">Contrato: {venda.codigo}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-green-600">{formatCurrency(venda.valor_solicitado)}</p>
                      <p className="text-xs text-gray-500">{venda.tipo_operacao}</p>
                      {getStatusBadge(venda)}
                    </div>
                  </div>
                  {/* Motivo se reprovado/estornado */}
                  {(venda.motivo_reprovacao || venda.motivo_estorno) && (
                    <div className="mt-2 ml-11 p-2 bg-red-50 rounded text-xs text-red-700">
                      <strong>Motivo:</strong> {venda.motivo_reprovacao || venda.motivo_estorno}
                    </div>
                  )}
                </div>
              ))}
              
              {clientesDoBanco.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <User className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  <p>Nenhum cliente encontrado</p>
                </div>
              )}
            </div>
          </ScrollArea>
          
          {/* Resumo */}
          {clientesDoBanco.length > 0 && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg border-t">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total de contratos:</span>
                <span className="font-semibold">{clientesDoBanco.length}</span>
              </div>
              <div className="flex justify-between items-center mt-1">
                <span className="text-sm text-gray-600">Valor total:</span>
                <span className="font-semibold text-green-600">
                  {formatCurrency(clientesDoBanco.reduce((acc, v) => acc + (v.valor_solicitado || 0), 0))}
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Configuração de Tags */}
      <Dialog open={showConfigTagsVendas} onOpenChange={setShowConfigTagsVendas}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tags className="w-5 h-5" />
              Configurar Tags de Contratos
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Lista de Tags Existentes */}
            <div className="space-y-2">
              <Label>Tags Existentes</Label>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {tagsContratos.map(tag => (
                  <div 
                    key={tag.id}
                    className="flex items-center justify-between p-3 rounded-lg border"
                    style={{ backgroundColor: tag.bgCor, borderColor: tag.cor }}
                  >
                    {editandoTagVendas?.id === tag.id ? (
                      <div className="flex items-center gap-2 flex-1">
                        <Input
                          value={editandoTagVendas.nome}
                          onChange={(e) => setEditandoTagVendas({ ...editandoTagVendas, nome: e.target.value })}
                          className="h-8"
                        />
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" size="sm" className="h-8 w-8 p-0">
                              <div 
                                className="w-4 h-4 rounded-full"
                                style={{ backgroundColor: editandoTagVendas.cor }}
                              />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-2">
                            <div className="grid grid-cols-5 gap-1">
                              {CORES_TAGS.map(corOpcao => (
                                <button
                                  key={corOpcao.cor}
                                  className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${editandoTagVendas.cor === corOpcao.cor ? 'ring-2 ring-offset-2' : ''}`}
                                  style={{ backgroundColor: corOpcao.cor }}
                                  onClick={() => setEditandoTagVendas({ 
                                    ...editandoTagVendas, 
                                    cor: corOpcao.cor, 
                                    bgCor: corOpcao.bgCor 
                                  })}
                                />
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                        <Button size="sm" onClick={atualizarTagVendas} disabled={salvandoTagVendas}>
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditandoTagVendas(null)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: tag.cor }}
                          />
                          <span style={{ color: tag.cor }} className="font-medium">
                            {tag.nome}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {contadorPorTagVendas[tag.id] || 0} contratos
                          </Badge>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => setEditandoTagVendas({ ...tag })}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-600"
                            onClick={() => excluirTagVendas(tag.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Adicionar Nova Tag */}
            <div className="space-y-2 pt-4 border-t">
              <Label>Adicionar Nova Tag</Label>
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Nome da tag..."
                  value={novaTagVendas.nome}
                  onChange={(e) => setNovaTagVendas({ ...novaTagVendas, nome: e.target.value })}
                  className="flex-1"
                />
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-10 h-10 p-0">
                      <div 
                        className="w-5 h-5 rounded-full"
                        style={{ backgroundColor: novaTagVendas.cor }}
                      />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-2">
                    <div className="grid grid-cols-5 gap-1">
                      {CORES_TAGS.map(corOpcao => (
                        <button
                          key={corOpcao.cor}
                          className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${novaTagVendas.cor === corOpcao.cor ? 'ring-2 ring-offset-2' : ''}`}
                          style={{ backgroundColor: corOpcao.cor }}
                          onClick={() => setNovaTagVendas({ 
                            ...novaTagVendas, 
                            cor: corOpcao.cor, 
                            bgCor: corOpcao.bgCor 
                          })}
                        />
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
                <Button onClick={criarTagVendas} disabled={salvandoTagVendas || !novaTagVendas.nome.trim()}>
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigTagsVendas(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Linha do Tempo do Contrato */}
      <TimelineTagsModal
        open={showTimelineContrato}
        onOpenChange={setShowTimelineContrato}
        entity={timelineContrato}
        tags={tagsContratos}
        mode="contrato"
      />
    </div>
  );
}
